import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
    Container, Box, Typography, IconButton, Paper, Button, Chip,
    Select, MenuItem, CircularProgress, Alert, Stack,
    TextField, ToggleButton, ToggleButtonGroup, FormControl, InputLabel,
    InputAdornment, Autocomplete, FormControlLabel, Checkbox,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useTheme, useMediaQuery, Tooltip, Menu, Collapse,
    List, ListItem, ListItemText, Divider, Popover,
    alpha
} from '@mui/material';
import FormDialog, { FormField, FormSection, arborGreen, filledInputSx } from '../components/FormDialog';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RepeatIcon from '@mui/icons-material/Repeat';
import EventIcon from '@mui/icons-material/Event';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import {
    RecurringInterval,
    ScheduledTransactionType,
    AccountType,
    ScheduledTransactionAction,
    Currency,
    CashFlowRange,
    InvestmentAccountCategory,
    INVESTMENT_ACCOUNT_CATEGORY_LABELS,
    CAD_INVESTMENT_ACCOUNT_CATEGORIES,
    USD_INVESTMENT_ACCOUNT_CATEGORIES,
} from '../constants/enums';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LoadingOverlay from '../components/LoadingOverlay';
import BalanceInput from '../components/BalanceInput';
import { handleNumericFocus } from '../utils/inputUtils';
import { getTodayInTimezone } from '../utils/date';
import { useTranslation } from 'react-i18next';
import { sortCategoriesHierarchically, getCategoryOptionLabel } from '../utils/category';

const formatDateSafe = (dateStr, lang = 'en-US') => {
    if (!dateStr) return '';
    const cleanStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    return new Date(cleanStr + 'T12:00:00').toLocaleDateString(lang, { year: 'numeric', month: 'short', day: 'numeric' });
};

const CustomAxisTooltip = ({ dataPoints, baseCurrency, formatCurrency }) => {
    const { t, i18n } = useTranslation();
    const tooltipData = useAxesTooltip();
    // In MUI X Charts v8, useAxesTooltip() returns an array of items per series
    if (!tooltipData || !Array.isArray(tooltipData) || tooltipData.length === 0) return null;

    // In v8.27.5, each item has dataIndex as a direct property (not nested under identifier)
    const dataIndex = tooltipData[0]?.dataIndex;
    if (dataIndex === undefined || dataIndex === null) return null;
    if (!dataPoints) return null;

    const dp = dataPoints[dataIndex];
    if (!dp) return null;

    return (
        <ChartsTooltipContainer>
            <Paper
                elevation={6}
                sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    maxWidth: 320,
                    minWidth: 260,
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                    backdropFilter: 'blur(8px)',
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
                    {t('Week of')} {formatDateSafe(dp.week_start, i18n.language)}
                </Typography>

                {/* Inflow Section */}
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {t('Total Inflow')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {formatCurrency(parseFloat(dp.credits), baseCurrency)}
                        </Typography>
                    </Stack>
                    <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 } }}>
                        {dp.inflow_items && dp.inflow_items.length > 0 ? (
                            dp.inflow_items.map((item, idx) => (
                                <Stack key={`in-${idx}`} direction="row" justifyContent="space-between" spacing={1} sx={{ py: 0.25 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }} title={item.description}>
                                        {item.description}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'success.light' }}>
                                        +{formatCurrency(item.amount, baseCurrency)}
                                    </Typography>
                                </Stack>
                            ))
                        ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'block', py: 0.25 }}>
                                {t('No inflows')}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Outflow Section */}
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {t('Total Outflow')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                            {formatCurrency(parseFloat(dp.debits), baseCurrency)}
                        </Typography>
                    </Stack>
                    <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 } }}>
                        {dp.outflow_items && dp.outflow_items.length > 0 ? (
                            dp.outflow_items.map((item, idx) => (
                                <Stack key={`out-${idx}`} direction="row" justifyContent="space-between" spacing={1} sx={{ py: 0.25 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }} title={item.description}>
                                        {item.description}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'error.light' }}>
                                        -{formatCurrency(item.amount, baseCurrency)}
                                    </Typography>
                                </Stack>
                            ))
                        ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'block', py: 0.25 }}>
                                {t('No outflows')}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
        </ChartsTooltipContainer>
    );
};

const CustomDailyAxisTooltip = ({ dataPoints, baseCurrency, formatCurrency }) => {
    const { t, i18n } = useTranslation();
    const tooltipData = useAxesTooltip();
    if (!tooltipData || !Array.isArray(tooltipData) || tooltipData.length === 0) return null;

    const dataIndex = tooltipData[0]?.dataIndex;
    if (dataIndex === undefined || dataIndex === null) return null;
    if (!dataPoints) return null;

    const dp = dataPoints[dataIndex];
    if (!dp) return null;

    const hasInflows = dp.inflow_items && dp.inflow_items.length > 0;
    const hasOutflows = dp.outflow_items && dp.outflow_items.length > 0;

    return (
        <ChartsTooltipContainer>
            <Paper
                elevation={6}
                sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    maxWidth: 320,
                    minWidth: 240,
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                    backdropFilter: 'blur(8px)',
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
                    {formatDateSafe(dp.date, i18n.language)}
                </Typography>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: (hasInflows || hasOutflows) ? 1.5 : 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {t('Running Balance')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {formatCurrency(parseFloat(dp.running_balance), baseCurrency)}
                    </Typography>
                </Stack>

                {hasInflows && (
                    <Box sx={{ mb: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('Inflow')}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main' }}>
                                +{formatCurrency(parseFloat(dp.credits), baseCurrency)}
                            </Typography>
                        </Stack>
                        <Box sx={{ maxHeight: 120, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 } }}>
                            {dp.inflow_items.map((item, idx) => (
                                <Stack key={`din-${idx}`} direction="row" justifyContent="space-between" spacing={1} sx={{ py: 0.2 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }} title={item.description}>
                                        {item.description}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'success.light' }}>
                                        +{formatCurrency(item.amount, baseCurrency)}
                                    </Typography>
                                </Stack>
                            ))}
                        </Box>
                    </Box>
                )}

                {hasOutflows && (
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('Outflow')}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main' }}>
                                -{formatCurrency(parseFloat(dp.debits), baseCurrency)}
                            </Typography>
                        </Stack>
                        <Box sx={{ maxHeight: 120, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 } }}>
                            {dp.outflow_items.map((item, idx) => (
                                <Stack key={`dout-${idx}`} direction="row" justifyContent="space-between" spacing={1} sx={{ py: 0.2 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }} title={item.description}>
                                        {item.description}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'error.light' }}>
                                        -{formatCurrency(item.amount, baseCurrency)}
                                    </Typography>
                                </Stack>
                            ))}
                        </Box>
                    </Box>
                )}
            </Paper>
        </ChartsTooltipContainer>
    );
};

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();

const INTERVAL_LABELS = {
    DAILY: 'Daily', WEEKLY: 'Weekly', BI_WEEKLY: 'Bi-weekly',
    SEMI_MONTHLY: 'Semi-monthly', MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly', SEMI_ANNUALLY: 'Semi-annually', YEARLY: 'Yearly',
    DAYS_OF_WEEK: 'Days of Week', DAYS_OF_MONTH: 'Days of Month',
};

const ACCOUNT_TYPE_ORDER = { CHECKING: 1, SAVINGS: 2, INVESTMENT: 3 };

const sortAccounts = (accList, preferredCurrency) => {
    if (!accList || !Array.isArray(accList)) return [];
    return [...accList].sort((a, b) => {
        const typeA = ACCOUNT_TYPE_ORDER[(a.account_type || '').toUpperCase()] || 99;
        const typeB = ACCOUNT_TYPE_ORDER[(b.account_type || '').toUpperCase()] || 99;
        if (typeA !== typeB) return typeA - typeB;

        const prefCurr = (preferredCurrency || 'CAD').toUpperCase();
        const currA = (a.currency || '').toUpperCase();
        const currB = (b.currency || '').toUpperCase();

        const isPrefA = currA === prefCurr ? 0 : 1;
        const isPrefB = currB === prefCurr ? 0 : 1;
        if (isPrefA !== isPrefB) return isPrefA - isPrefB;

        return currA.localeCompare(currB);
    });
};

const formatAccountDisplayName = (stat, acc, t) => {
    if (acc) {
        return `${t(acc.account_type)} (${acc.currency})`;
    }
    const rawName = stat?.account_name || '';
    if (!rawName) return '';
    const match = rawName.match(/^(.+?)\s*\((.+?)\)$/);
    if (match) {
        return `${t(match[1])} (${match[2]})`;
    }
    return t(rawName);
};

const EMPTY_FORM = {
    description: '',
    amount: '',
    currency: 'CAD',
    payment_type: ScheduledTransactionType.RECURRING,
    flow_direction: 'debit', // 'credit' | 'debit' — UI helper, not sent to API
    interval: RecurringInterval.MONTHLY,
    interval_config: null,
    start_date: getTodayInTimezone(),
    end_date: '',
    is_active: true,
    category_id: '',
    action: ScheduledTransactionAction.OUTFLOW,
    from_account_id: '',
    to_account_id: '',
};

// ─────────────────────────────────────────────
// Components
// ─────────────────────────────────────────────


const EditableContributionRow = React.memo(({
    item,
    currency,
    categoryOptions,
    isUsdToCad,
    cadRate,
    formatCurrency,
    onUpdate,
    onDelete,
    isViewer,
    isUpdating,
    showToast,
    t,
}) => {
    const rawDate = item.contribution_date ? item.contribution_date.split('T')[0] : '';
    const [dateVal, setDateVal] = useState(rawDate);
    const [categoryVal, setCategoryVal] = useState(item.account_category || '');
    const [amountVal, setAmountVal] = useState(new Intl.NumberFormat('en-US').format(item.amount || 0));
    const [descVal, setDescVal] = useState(item.description || '');

    useEffect(() => {
        const clean = item.contribution_date ? item.contribution_date.split('T')[0] : '';
        setDateVal(clean);
        setCategoryVal(item.account_category || '');
        setAmountVal(new Intl.NumberFormat('en-US').format(item.amount || 0));
        setDescVal(item.description || '');
    }, [item.id, item.contribution_date, item.account_category, item.amount, item.description]);

    const handleDateBlur = () => {
        if (dateVal && dateVal !== rawDate) {
            onUpdate(item.id, { contribution_date: dateVal });
        }
    };

    const handleCategoryChange = (e) => {
        const newCat = e.target.value;
        setCategoryVal(newCat);
        if (newCat !== item.account_category) {
            onUpdate(item.id, { account_category: newCat });
        }
    };

    const handleAmountBlur = () => {
        const raw = amountVal.replace(/,/g, '');
        const num = parseFloat(raw);
        if (isNaN(num) || num <= 0) {
            setAmountVal(new Intl.NumberFormat('en-US').format(item.amount || 0));
            if (raw !== '') {
                showToast(t('Please enter a valid amount greater than 0'), 'error');
            }
            return;
        }
        setAmountVal(new Intl.NumberFormat('en-US').format(num));
        if (Math.abs(num - parseFloat(item.amount)) > 0.0001) {
            onUpdate(item.id, { amount: num.toFixed(2) });
        }
    };

    const handleDescBlur = () => {
        const trimmed = descVal.trim();
        const prevTrimmed = (item.description || '').trim();
        if (trimmed !== prevTrimmed) {
            onUpdate(item.id, { description: trimmed || null });
        }
    };

    const numAmount = parseFloat(item.amount) || 0;

    return (
        <TableRow hover sx={{ '& td': { py: 0.5, px: 0.75, borderBottom: '1px solid', borderColor: 'divider' } }}>
            {/* Date Cell */}
            <TableCell sx={{ minWidth: 125, width: '20%' }}>
                <TextField
                    type="date"
                    size="small"
                    variant="standard"
                    value={dateVal}
                    disabled={isViewer || isUpdating}
                    onChange={(e) => setDateVal(e.target.value)}
                    onBlur={handleDateBlur}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                            fontSize: '0.78rem',
                            height: 32,
                            borderRadius: '4px',
                            px: 0.5,
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08) },
                            '&.Mui-focused': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                        }
                    }}
                />
            </TableCell>

            {/* Category Cell */}
            <TableCell sx={{ minWidth: 135, width: '22%' }}>
                <Select
                    size="small"
                    variant="standard"
                    value={categoryVal}
                    disabled={isViewer || isUpdating}
                    onChange={handleCategoryChange}
                    disableUnderline
                    sx={{
                        width: '100%',
                        fontSize: '0.78rem',
                        height: 32,
                        borderRadius: '4px',
                        px: 0.5,
                        '&:hover': { bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08) },
                        '&.Mui-focused': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                    }}
                >
                    {categoryOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </TableCell>

            {/* Amount Cell */}
            <TableCell align="right" sx={{ minWidth: 110, width: '18%' }}>
                <TextField
                    size="small"
                    variant="standard"
                    value={amountVal}
                    disabled={isViewer || isUpdating}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^[0-9,.]*$/.test(val)) setAmountVal(val);
                    }}
                    onFocus={(e) => handleNumericFocus(e, (ev) => setAmountVal(ev.target.value.replace(/,/g, '')))}
                    onBlur={handleAmountBlur}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    InputProps={{
                        disableUnderline: true,
                        startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 0.25 }}>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'success.main' }}>
                                    +$
                                </Typography>
                            </InputAdornment>
                        )
                    }}
                    slotProps={{
                        htmlInput: {
                            style: { textAlign: 'right', fontWeight: 700, fontSize: '0.82rem', color: arborGreen }
                        }
                    }}
                    sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                            height: 32,
                            borderRadius: '4px',
                            px: 0.5,
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08) },
                            '&.Mui-focused': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                        }
                    }}
                />
                {isUsdToCad && cadRate && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400, fontSize: '0.66rem', lineHeight: 1.1, textAlign: 'right', mt: -0.25, pr: 0.5 }}>
                        ≈ {formatCurrency(numAmount * cadRate, 'CAD')}
                    </Typography>
                )}
            </TableCell>

            {/* Description Cell */}
            <TableCell sx={{ minWidth: 160, width: '32%' }}>
                <TextField
                    size="small"
                    variant="standard"
                    placeholder={t('Add notes...')}
                    value={descVal}
                    disabled={isViewer || isUpdating}
                    onChange={(e) => setDescVal(e.target.value)}
                    onBlur={handleDescBlur}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                            fontSize: '0.78rem',
                            height: 32,
                            borderRadius: '4px',
                            px: 0.5,
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08) },
                            '&.Mui-focused': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                        }
                    }}
                />
            </TableCell>

            {/* Action (Delete) Cell */}
            <TableCell align="center" sx={{ width: '8%', minWidth: 36, px: 0.25 }}>
                {isUpdating ? (
                    <CircularProgress size={16} />
                ) : (
                    <IconButton
                        size="small"
                        color="error"
                        disabled={isViewer}
                        onClick={() => onDelete(item)}
                        sx={{
                            p: 0.5,
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.08) }
                        }}
                        aria-label="delete-contribution"
                    >
                        <DeleteIcon sx={{ fontSize: '0.92rem' }} />
                    </IconButton>
                )}
            </TableCell>
        </TableRow>
    );
});


const EditableContributionCard = React.memo(({
    item,
    currency,
    categoryOptions,
    isUsdToCad,
    cadRate,
    formatCurrency,
    onUpdate,
    onDelete,
    isViewer,
    isUpdating,
    showToast,
    t,
}) => {
    const rawDate = item.contribution_date ? item.contribution_date.split('T')[0] : '';
    const [dateVal, setDateVal] = useState(rawDate);
    const [categoryVal, setCategoryVal] = useState(item.account_category || '');
    const [amountVal, setAmountVal] = useState(new Intl.NumberFormat('en-US').format(item.amount || 0));
    const [descVal, setDescVal] = useState(item.description || '');

    useEffect(() => {
        const clean = item.contribution_date ? item.contribution_date.split('T')[0] : '';
        setDateVal(clean);
        setCategoryVal(item.account_category || '');
        setAmountVal(new Intl.NumberFormat('en-US').format(item.amount || 0));
        setDescVal(item.description || '');
    }, [item.id, item.contribution_date, item.account_category, item.amount, item.description]);

    const handleDateBlur = () => {
        if (dateVal && dateVal !== rawDate) {
            onUpdate(item.id, { contribution_date: dateVal });
        }
    };

    const handleCategoryChange = (e) => {
        const newCat = e.target.value;
        setCategoryVal(newCat);
        if (newCat !== item.account_category) {
            onUpdate(item.id, { account_category: newCat });
        }
    };

    const handleAmountBlur = () => {
        const raw = amountVal.replace(/,/g, '');
        const num = parseFloat(raw);
        if (isNaN(num) || num <= 0) {
            setAmountVal(new Intl.NumberFormat('en-US').format(item.amount || 0));
            if (raw !== '') {
                showToast(t('Please enter a valid amount greater than 0'), 'error');
            }
            return;
        }
        setAmountVal(new Intl.NumberFormat('en-US').format(num));
        if (Math.abs(num - parseFloat(item.amount)) > 0.0001) {
            onUpdate(item.id, { amount: num.toFixed(2) });
        }
    };

    const handleDescBlur = () => {
        const trimmed = descVal.trim();
        const prevTrimmed = (item.description || '').trim();
        if (trimmed !== prevTrimmed) {
            onUpdate(item.id, { description: trimmed || null });
        }
    };

    const numAmount = parseFloat(item.amount) || 0;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: '14px',
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.divider, 0.8),
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.common.black, 0.03)}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                position: 'relative'
            }}
        >
            {/* Top row: Amount with currency + Trash Delete */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1, mr: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.72rem', display: 'block', mb: 0.25 }}>
                        {t('Amount')}
                    </Typography>
                    <TextField
                        size="small"
                        variant="standard"
                        value={amountVal}
                        disabled={isViewer || isUpdating}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^[0-9,.]*$/.test(val)) setAmountVal(val);
                        }}
                        onFocus={(e) => handleNumericFocus(e, (ev) => setAmountVal(ev.target.value.replace(/,/g, '')))}
                        onBlur={handleAmountBlur}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        InputProps={{
                            disableUnderline: true,
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.25 }}>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'success.main' }}>
                                        +$
                                    </Typography>
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary' }}>
                                        {currency}
                                    </Typography>
                                </InputAdornment>
                            )
                        }}
                        slotProps={{
                            htmlInput: {
                                inputMode: 'decimal',
                                style: { fontWeight: 800, fontSize: '1.05rem', color: arborGreen }
                            }
                        }}
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                                height: 38,
                                borderRadius: '6px',
                                px: 1,
                                bgcolor: (theme) => alpha(theme.palette.success.main, 0.04),
                                '&:hover': { bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08) },
                                '&.Mui-focused': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                            }
                        }}
                    />
                    {isUsdToCad && cadRate && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem', mt: 0.25, pl: 0.5 }}>
                            ≈ {formatCurrency(numAmount * cadRate, 'CAD')}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ flexShrink: 0, pt: 2 }}>
                    {isUpdating ? (
                        <CircularProgress size={20} />
                    ) : (
                        <IconButton
                            size="small"
                            color="error"
                            disabled={isViewer}
                            onClick={() => onDelete(item)}
                            sx={{
                                p: 1,
                                bgcolor: (theme) => alpha(theme.palette.error.main, 0.06),
                                '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.12) }
                            }}
                            aria-label="delete-contribution"
                        >
                            <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Middle row: Category & Date */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.72rem', display: 'block', mb: 0.25 }}>
                        {t('Account Type')}
                    </Typography>
                    <Select
                        size="small"
                        variant="standard"
                        value={categoryVal}
                        disabled={isViewer || isUpdating}
                        onChange={handleCategoryChange}
                        disableUnderline
                        sx={{
                            width: '100%',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            height: 36,
                            borderRadius: '6px',
                            px: 1,
                            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08) },
                            '&.Mui-focused': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                        }}
                    >
                        {categoryOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.82rem' }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.72rem', display: 'block', mb: 0.25 }}>
                        {t('Date')}
                    </Typography>
                    <TextField
                        type="date"
                        size="small"
                        variant="standard"
                        value={dateVal}
                        disabled={isViewer || isUpdating}
                        onChange={(e) => setDateVal(e.target.value)}
                        onBlur={handleDateBlur}
                        InputProps={{ disableUnderline: true }}
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                                fontSize: '0.8rem',
                                height: 36,
                                borderRadius: '6px',
                                px: 1,
                                bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
                                '&:hover': { bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08) },
                                '&.Mui-focused': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                            }
                        }}
                    />
                </Box>
            </Box>

            {/* Bottom row: Description */}
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.72rem', display: 'block', mb: 0.25 }}>
                    {t('Description')}
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    placeholder={t('Add notes...')}
                    value={descVal}
                    disabled={isViewer || isUpdating}
                    onChange={(e) => setDescVal(e.target.value)}
                    onBlur={handleDescBlur}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                            fontSize: '0.8rem',
                            height: 36,
                            borderRadius: '6px',
                            px: 1,
                            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04),
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.action.hover, 0.08) },
                            '&.Mui-focused': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                        }
                    }}
                />
            </Box>
        </Paper>
    );
});


const AddContributionDialog = React.memo(({
    open,
    onClose,
    account,
    currentBalance = 0,
    formatCurrency,
    ledgerId,
    api,
    preferredCurrency,
    preferredTimezone,
    onSuccess,
    showToast,
    userRole,
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isViewer = userRole === 'viewer';

    const [category, setCategory] = useState('');
    const [date, setDate] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // History state
    const [contributionsSummary, setContributionsSummary] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // USD to CAD exchange rate state
    const [cadRate, setCadRate] = useState(null);
    const [loadingRate, setLoadingRate] = useState(false);

    // Sync confirmation modal
    const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
    const [syncing, setSyncing] = useState(false);

    // Delete confirmation modal
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [contribToDelete, setContribToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Direct updating contribution state
    const [updatingContribId, setUpdatingContribId] = useState(null);

    const currency = account?.currency || 'CAD';
    const isUsdToCad = currency === 'USD' && preferredCurrency === 'CAD';

    const categoryOptions = useMemo(() => {
        if (!account) return [];
        const cats = account.currency === 'USD'
            ? USD_INVESTMENT_ACCOUNT_CATEGORIES
            : CAD_INVESTMENT_ACCOUNT_CATEGORIES;
        return cats.map((cat) => ({
            value: cat,
            label: INVESTMENT_ACCOUNT_CATEGORY_LABELS[cat] || cat,
        }));
    }, [account]);

    const fetchContributions = useCallback(async () => {
        if (!account?.id || !ledgerId || !api) return;
        setLoadingHistory(true);
        try {
            const res = await api.get(`/ledger/ledgers/${ledgerId}/accounts/${account.id}/contributions`);
            setContributionsSummary(res.data);
        } catch (err) {
            console.error('Failed to fetch contributions', err);
        } finally {
            setLoadingHistory(false);
        }
    }, [account?.id, ledgerId, api]);

    const fetchCadRate = useCallback(async () => {
        if (!isUsdToCad || !ledgerId || !api) {
            setCadRate(null);
            return;
        }
        setLoadingRate(true);
        try {
            const res = await api.get(`/ledger/ledgers/${ledgerId}/budget/exchange-rates`, {
                params: { from_currencies: 'USD', to_currency: 'CAD' }
            });
            if (res.data?.rates?.USD) {
                setCadRate(parseFloat(res.data.rates.USD));
            }
        } catch (err) {
            console.warn('Failed to fetch USD to CAD rate', err);
        } finally {
            setLoadingRate(false);
        }
    }, [isUsdToCad, ledgerId, api]);

    useEffect(() => {
        if (open && account) {
            setAmount('');
            setDescription('');
            setSubmitting(false);
            setDate(getTodayInTimezone(preferredTimezone));
            const defaultCat = account.currency === 'USD'
                ? InvestmentAccountCategory.F401K
                : InvestmentAccountCategory.TFSA;
            setCategory(defaultCat);
            fetchContributions();
            fetchCadRate();
        }
    }, [open, account, preferredTimezone, fetchContributions, fetchCadRate]);

    const numAmount = parseFloat(amount.replace(/,/g, '')) || 0;
    const currentNum = contributionsSummary
        ? parseFloat(contributionsSummary.current_balance) || 0
        : parseFloat(currentBalance) || 0;
    const newBalance = currentNum + numAmount;

    const approxCadVal = useMemo(() => {
        if (isUsdToCad && cadRate && numAmount > 0) {
            return numAmount * cadRate;
        }
        return null;
    }, [isUsdToCad, cadRate, numAmount]);

    const handlePresetClick = (presetVal) => {
        const currentVal = parseFloat(amount.replace(/,/g, '')) || 0;
        const nextVal = currentVal + presetVal;
        setAmount(new Intl.NumberFormat('en-US').format(nextVal));
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (numAmount <= 0 || submitting || !account || isViewer) return;
        setSubmitting(true);
        try {
            const payload = {
                amount: numAmount,
                account_category: category,
                contribution_date: date || getTodayInTimezone(preferredTimezone),
                description: description.trim() || null,
            };
            const res = await api.post(`/ledger/ledgers/${ledgerId}/accounts/${account.id}/contributions`, payload);
            if (showToast) {
                showToast(t('Added contribution of {{amount}} to {{account}}. New balance: {{balance}}', {
                    amount: formatCurrency(numAmount, currency),
                    account: formatAccountDisplayName(null, account, t),
                    balance: formatCurrency(res.data.new_account_balance ?? newBalance, currency)
                }), 'success');
            }
            setAmount('');
            setDescription('');
            await Promise.all([fetchContributions(), onSuccess && onSuccess()]);
        } catch (err) {
            console.error('Failed to add contribution', err);
            if (showToast) {
                showToast(`Error: ${err.response?.data?.detail || t('Failed to add contribution')}`, 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmSync = async () => {
        if (!account || isViewer) return;
        setSyncing(true);
        try {
            const res = await api.post(`/ledger/ledgers/${ledgerId}/accounts/${account.id}/contributions/sync-balance`);
            if (showToast) {
                showToast(t('Account balance synced to contribution total: {{amount}}', {
                    amount: formatCurrency(res.data.current_balance, currency)
                }), 'success');
            }
            setContributionsSummary(res.data);
            setSyncConfirmOpen(false);
            if (onSuccess) await onSuccess();
        } catch (err) {
            console.error('Failed to sync balance', err);
            if (showToast) {
                showToast(`Error: ${err.response?.data?.detail || t('Failed to sync balance')}`, 'error');
            }
        } finally {
            setSyncing(false);
        }
    };

    const handleDeleteContribution = async () => {
        if (!account || !contribToDelete || isViewer) return;
        setDeleting(true);
        try {
            await api.delete(`/ledger/ledgers/${ledgerId}/accounts/${account.id}/contributions/${contribToDelete.id}`);
            if (showToast) {
                showToast(t('Contribution deleted.'), 'success');
            }
            setDeleteConfirmOpen(false);
            setContribToDelete(null);
            await Promise.all([fetchContributions(), onSuccess && onSuccess()]);
        } catch (err) {
            console.error('Failed to delete contribution', err);
            if (showToast) {
                showToast(`Error: ${err.response?.data?.detail || t('Failed to delete contribution')}`, 'error');
            }
        } finally {
            setDeleting(false);
        }
    };

    const handleDirectUpdateContribution = async (contribId, patch) => {
        if (!account || isViewer) return;
        setUpdatingContribId(contribId);
        // Optimistically update local contributionsSummary state so category movement is instantaneous
        if (patch.account_category || patch.amount !== undefined || patch.contribution_date || patch.description !== undefined) {
            setContributionsSummary((prev) => {
                if (!prev) return prev;
                const updatedList = (prev.contributions || []).map((c) =>
                    c.id === contribId ? { ...c, ...patch } : c
                );
                return { ...prev, contributions: updatedList };
            });
        }
        try {
            await api.put(`/ledger/ledgers/${ledgerId}/accounts/${account.id}/contributions/${contribId}`, patch);
            await Promise.all([fetchContributions(), onSuccess && onSuccess()]);
        } catch (err) {
            console.error('Failed to update contribution', err);
            if (showToast) {
                showToast(`Error: ${err.response?.data?.detail || t('Failed to update contribution')}`, 'error');
            }
            await fetchContributions();
        } finally {
            setUpdatingContribId(null);
        }
    };

    const contributionsList = useMemo(() => contributionsSummary?.contributions || [], [contributionsSummary]);
    const isInSync = contributionsSummary ? contributionsSummary.is_in_sync : true;
    const contributionsTotal = contributionsSummary ? parseFloat(contributionsSummary.contributions_total) || 0 : 0;
    const balanceDiff = currentNum - contributionsTotal;

    const groupedContributions = useMemo(() => {
        const groupsMap = {};
        contributionsList.forEach((item) => {
            const cat = item.account_category || 'OTHER';
            if (!groupsMap[cat]) {
                const opt = categoryOptions.find((o) => o.value === cat);
                groupsMap[cat] = {
                    category: cat,
                    label: opt?.label || INVESTMENT_ACCOUNT_CATEGORY_LABELS[cat] || cat,
                    items: [],
                    total: 0,
                };
            }
            groupsMap[cat].items.push(item);
            groupsMap[cat].total += parseFloat(item.amount) || 0;
        });

        const order = categoryOptions.map((opt) => opt.value);
        return Object.values(groupsMap).sort((a, b) => {
            const idxA = order.indexOf(a.category);
            const idxB = order.indexOf(b.category);
            if (idxA !== -1 && idxB !== -1) return idxA - idxB;
            if (idxA !== -1) return -1;
            if (idxB !== -1) return 1;
            return a.label.localeCompare(b.label);
        });
    }, [contributionsList, categoryOptions]);

    return (
        <>
            <FormDialog
                open={open}
                onClose={onClose}
                onSubmit={handleSubmit}
                title={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <span>{t('Investment Contribution')}</span>
                        {account && (
                            <Chip
                                label={`${t(account.account_type)} (${account.currency})`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                                    color: 'primary.main',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05)
                                }}
                            />
                        )}
                    </Box>
                }
                subtitle={t('Enter the contribution amount to add to your investment account balance.')}
                saveLabel={t('Add to Balance')}
                cancelLabel={t('Close')}
                saving={submitting}
                saveDisabled={numAmount <= 0 || submitting || isViewer}
                maxWidth="md"
            >
                <Stack spacing={3}>
                    {/* Current Balance card */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.12)
                    }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                {t('Current Balance')}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', fontFamily: 'Manrope, sans-serif' }}>
                                {formatCurrency(currentNum, currency)} {currency}
                            </Typography>
                        </Box>
                        <Chip
                            label={currency}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main',
                                borderRadius: '8px'
                            }}
                        />
                    </Box>

                    {/* Form Fields Section */}
                    <FormSection title={t("Contribution Details")}>
                        <Stack spacing={2.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                {/* Account Type Select */}
                                <FormField label={t("Account Type")} required sx={{ flex: 1 }}>
                                    <TextField
                                        select
                                        variant="filled"
                                        fullWidth
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        disabled={isViewer || submitting}
                                        sx={filledInputSx}
                                        SelectProps={{ disableUnderline: true }}
                                    >
                                        {categoryOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </FormField>

                                {/* Contribution Date */}
                                <FormField label={t("Contribution Date")} required sx={{ flex: 1 }}>
                                    <TextField
                                        type="date"
                                        variant="filled"
                                        fullWidth
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        disabled={isViewer || submitting}
                                        sx={filledInputSx}
                                        InputProps={{ disableUnderline: true }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </FormField>
                            </Stack>

                            {/* Contribution Amount */}
                            <FormField label={t("Contribution Amount")} required>
                                <TextField
                                    autoFocus
                                    variant="filled"
                                    fullWidth
                                    value={amount}
                                    disabled={isViewer || submitting}
                                    onFocus={(e) => handleNumericFocus(e, (ev) => {
                                        const raw = ev.target.value.replace(/,/g, '');
                                        if (raw) setAmount(raw);
                                    })}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^[0-9,.]*$/.test(val)) {
                                            setAmount(val);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (numAmount > 0) {
                                            setAmount(new Intl.NumberFormat('en-US').format(numAmount));
                                        }
                                    }}
                                    placeholder="0.00"
                                    sx={filledInputSx}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ color: arborGreen, fontWeight: 700, height: '100%', mt: 0 }}>
                                                $
                                            </InputAdornment>
                                        ),
                                        disableUnderline: true
                                    }}
                                    slotProps={{
                                        htmlInput: {
                                            inputMode: 'decimal',
                                            style: { textAlign: 'right', fontWeight: 600, fontSize: '1.05rem' }
                                        }
                                    }}
                                />
                            </FormField>

                            {/* USD to CAD Live Conversion Display */}
                            {isUsdToCad && (
                                <Box sx={{
                                    p: 1.5,
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 0.5
                                }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                        {t('Approx. CAD equivalent')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                        {approxCadVal !== null ? (
                                            `≈ ${formatCurrency(approxCadVal, 'CAD')} CAD ${cadRate ? `(@ 1 USD = ${cadRate.toFixed(4)} CAD)` : ''}`
                                        ) : (
                                            cadRate ? `${t('Approx. CAD rate')}: 1 USD = ${cadRate.toFixed(4)} CAD` : (loadingRate ? t('Fetching exchange rate...') : '')
                                        )}
                                    </Typography>
                                </Box>
                            )}

                            {/* Quick Add Presets */}
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75, fontWeight: 600 }}>
                                    {t('Quick Add')}:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {[1000, 2000, 3000, 5000, 10000].map((preset) => (
                                        <Button
                                            key={preset}
                                            size="small"
                                            variant="outlined"
                                            disabled={isViewer || submitting}
                                            onClick={() => handlePresetClick(preset)}
                                            sx={{
                                                py: 0.5,
                                                px: 1.5,
                                                fontSize: '0.75rem',
                                                minWidth: 'auto',
                                                borderRadius: '20px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                color: 'primary.main',
                                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                                                '&:hover': {
                                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                                    borderColor: 'primary.main',
                                                }
                                            }}
                                        >
                                            +${preset.toLocaleString()}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>

                            {/* Description Field */}
                            <FormField label={t("Description / Notes (Optional)")}>
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    multiline
                                    minRows={2}
                                    maxRows={3}
                                    placeholder={t('e.g., Monthly automated payroll contribution, bonus, rebalancing')}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={isViewer || submitting}
                                    sx={filledInputSx}
                                    InputProps={{ disableUnderline: true }}
                                />
                            </FormField>

                            {/* Calculation Preview */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, numAmount > 0 ? 0.06 : 0.03),
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.primary.main, numAmount > 0 ? 0.25 : 0.1),
                                    borderRadius: '16px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                    {t('Calculation Preview')}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                        {formatCurrency(currentNum, currency)} + {formatCurrency(numAmount, currency)}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: numAmount > 0 ? 'primary.main' : 'text.primary', fontFamily: 'Manrope, sans-serif' }}>
                                        = {formatCurrency(newBalance, currency)} {currency}
                                    </Typography>
                                </Box>
                                {isUsdToCad && approxCadVal !== null && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block', textAlign: 'right' }}>
                                        {t('Approx. CAD equivalent')}: ≈ {formatCurrency(approxCadVal, 'CAD')} CAD
                                    </Typography>
                                )}
                            </Paper>
                        </Stack>
                    </FormSection>

                    <Divider sx={{ my: 0.5 }} />

                    {/* Bottom Section: Contribution History */}
                    <FormSection title={t("Contribution History")}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {t('Recorded Contributions')} ({contributionsSummary?.contributions_count || 0})
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {t('Total Recorded')}: <strong>{formatCurrency(contributionsTotal, currency)} {currency}</strong>
                                </Typography>
                            </Box>

                            {!isInSync && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<SyncIcon />}
                                    disabled={isViewer || syncing}
                                    onClick={() => setSyncConfirmOpen(true)}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        borderRadius: '20px',
                                        py: 0.5,
                                        px: 1.5,
                                        width: isMobile ? '100%' : 'auto',
                                    }}
                                >
                                    {t('Sync Balance to Total')}
                                </Button>
                            )}
                        </Box>

                        {/* Out-of-sync alert banner */}
                        {!isInSync && (
                            <Alert
                                severity="warning"
                                sx={{ mb: 1.5, borderRadius: 2, py: 0.25, fontSize: '0.82rem' }}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        disabled={isViewer || syncing}
                                        onClick={() => setSyncConfirmOpen(true)}
                                        sx={{ fontWeight: 700, textTransform: 'none' }}
                                    >
                                        {t('Sync')}
                                    </Button>
                                }
                            >
                                {t('Account balance ({{current}}) differs from total contributions ({{total}}).', {
                                    current: `${formatCurrency(currentNum, currency)} ${currency}`,
                                    total: `${formatCurrency(contributionsTotal, currency)} ${currency}`
                                })}
                            </Alert>
                        )}

                        {/* Contributions List (Mobile Cards vs Desktop Table) */}
                        {loadingHistory ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : contributionsList.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center', fontStyle: 'italic' }}>
                                {t('No contributions recorded yet.')}
                            </Typography>
                        ) : isMobile ? (
                            <Stack spacing={2} sx={{ maxHeight: 380, overflowY: 'auto', pr: 0.5 }}>
                                {groupedContributions.map((group) => (
                                    <Box key={group.category}>
                                        {/* Category Subheader Banner */}
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 1,
                                            px: 1.25,
                                            py: 0.75,
                                            borderRadius: '10px',
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                                            border: '1px solid',
                                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={group.label}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ fontWeight: 700, fontSize: '0.75rem', height: 24 }}
                                                />
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                    ({group.items.length})
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8rem' }}>
                                                {formatCurrency(group.total, currency)} {currency}
                                            </Typography>
                                        </Box>

                                        {/* Cards in this category */}
                                        <Stack spacing={1}>
                                            {group.items.map((item) => (
                                                <EditableContributionCard
                                                    key={item.id}
                                                    item={item}
                                                    currency={currency}
                                                    categoryOptions={categoryOptions}
                                                    isUsdToCad={isUsdToCad}
                                                    cadRate={cadRate}
                                                    formatCurrency={formatCurrency}
                                                    onUpdate={handleDirectUpdateContribution}
                                                    onDelete={(it) => {
                                                        setContribToDelete(it);
                                                        setDeleteConfirmOpen(true);
                                                    }}
                                                    isViewer={isViewer}
                                                    isUpdating={updatingContribId === item.id}
                                                    showToast={showToast}
                                                    t={t}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <TableContainer
                                component={Paper}
                                elevation={0}
                                sx={{
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.divider, 0.8),
                                    borderRadius: '16px',
                                    maxHeight: 280,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    '&::-webkit-scrollbar': {
                                        width: '6px',
                                        height: '0px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.15),
                                        borderRadius: '3px',
                                    },
                                    scrollbarWidth: 'thin',
                                }}
                            >
                                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '20%', minWidth: 120, px: 1, py: 0.75, fontWeight: 700, fontSize: '0.75rem', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03) }}>
                                                {t('Date')}
                                            </TableCell>
                                            <TableCell sx={{ width: '22%', minWidth: 130, px: 1, py: 0.75, fontWeight: 700, fontSize: '0.75rem', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03) }}>
                                                {t('Account Type')}
                                            </TableCell>
                                            <TableCell align="right" sx={{ width: '18%', minWidth: 110, px: 1, py: 0.75, fontWeight: 700, fontSize: '0.75rem', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03) }}>
                                                {t('Amount')}
                                            </TableCell>
                                            <TableCell sx={{ width: '32%', minWidth: 140, px: 1, py: 0.75, fontWeight: 700, fontSize: '0.75rem', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03) }}>
                                                {t('Description')}
                                            </TableCell>
                                            <TableCell align="center" sx={{ width: '8%', minWidth: 40, px: 0.5, py: 0.75, fontWeight: 700, fontSize: '0.75rem', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03) }}>
                                                {/* Action */}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groupedContributions.map((group) => (
                                            <React.Fragment key={group.category}>
                                                {/* Category Subheader Row */}
                                                <TableRow
                                                    sx={{
                                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                                        borderTop: '1px solid',
                                                        borderBottom: '1px solid',
                                                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                                                    }}
                                                >
                                                    <TableCell colSpan={5} sx={{ py: 0.75, px: 1.5 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Chip
                                                                    label={group.label}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ fontWeight: 700, fontSize: '0.75rem', height: 22 }}
                                                                />
                                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                                    ({group.items.length} {group.items.length === 1 ? t('contribution') : t('contributions')})
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                                {t('Subtotal')}: <strong>{formatCurrency(group.total, currency)} {currency}</strong>
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>

                                                {/* Category Items */}
                                                {group.items.map((item) => (
                                                    <EditableContributionRow
                                                        key={item.id}
                                                        item={item}
                                                        currency={currency}
                                                        categoryOptions={categoryOptions}
                                                        isUsdToCad={isUsdToCad}
                                                        cadRate={cadRate}
                                                        formatCurrency={formatCurrency}
                                                        onUpdate={handleDirectUpdateContribution}
                                                        onDelete={(it) => {
                                                            setContribToDelete(it);
                                                            setDeleteConfirmOpen(true);
                                                        }}
                                                        isViewer={isViewer}
                                                        isUpdating={updatingContribId === item.id}
                                                        showToast={showToast}
                                                        t={t}
                                                    />
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </FormSection>
                </Stack>
            </FormDialog>

            {/* ── Sync Balance Confirmation Dialog ── */}
            <FormDialog
                open={syncConfirmOpen}
                onClose={syncing ? undefined : () => setSyncConfirmOpen(false)}
                onSubmit={handleConfirmSync}
                title={t("Sync Account Balance?")}
                subtitle={t("Reset account starting balance to match the total of all recorded contributions.")}
                saveLabel={t("Confirm & Sync Balance")}
                cancelLabel={t("Cancel")}
                saving={syncing}
                maxWidth="xs"
            >
                <Stack spacing={2}>
                    <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.85rem' }}>
                        {t('Are you sure you want to update the account balance from {{current}} to {{total}}? This will reset the account starting balance to match the total of all recorded contributions.', {
                            current: `${formatCurrency(currentNum, currency)} ${currency}`,
                            total: `${formatCurrency(contributionsTotal, currency)} ${currency}`
                        })}
                    </Alert>
                    <Box sx={{
                        p: 2,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600 }}>{t('Current Balance')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(currentNum, currency)} {currency}</Typography>
                        </Box>
                        <Typography sx={{ color: 'text.disabled', fontWeight: 700, fontSize: '1.2rem' }}>→</Typography>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600 }}>{t('Contributions Total')}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                {formatCurrency(contributionsTotal, currency)} {currency}
                                <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: balanceDiff > 0 ? 'error.main' : 'success.main' }}>
                                    ({balanceDiff > 0 ? '-' : '+'}{formatCurrency(Math.abs(balanceDiff), currency)})
                                </Typography>
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </FormDialog>

            {/* ── Delete Contribution Confirmation Dialog ── */}
            <FormDialog
                open={deleteConfirmOpen}
                onClose={deleting ? undefined : () => setDeleteConfirmOpen(false)}
                onSubmit={handleDeleteContribution}
                title={t("Delete Contribution?")}
                subtitle={t("Are you sure you want to delete this contribution record?")}
                saveLabel={t("Delete")}
                cancelLabel={t("Cancel")}
                saving={deleting}
                maxWidth="xs"
            >
                <Stack spacing={2}>
                    <Alert severity="error" sx={{ borderRadius: 2, fontSize: '0.85rem' }}>
                        {t("This action cannot be undone. The contribution amount will be deducted from your account balance.")}
                    </Alert>
                    {contribToDelete && (
                        <Box sx={{
                            p: 2,
                            bgcolor: (theme) => alpha(theme.palette.error.main, 0.04),
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.error.main, 0.15)
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {formatDateSafe(contribToDelete.contribution_date, i18n.language)} — {INVESTMENT_ACCOUNT_CATEGORY_LABELS[contribToDelete.account_category] || contribToDelete.account_category}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'error.main', mt: 0.5 }}>
                                -{formatCurrency(parseFloat(contribToDelete.amount), currency)} {currency}
                            </Typography>
                            {contribToDelete.description && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                                    {contribToDelete.description}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Stack>
            </FormDialog>
        </>
    );
});

const ScheduledPaymentRow = React.memo(({ pmt, formatDateSafe, formatCurrency, onToggleStatus, onOpenMenu, isViewer }) => {
    const { t, i18n } = useTranslation();
    return (
        <TableRow
            hover
            sx={{ opacity: pmt.is_active ? 1 : 0.5, '&:last-child td': { border: 0 } }}
        >
            <TableCell>
                {isViewer ? (
                    <Chip
                        size="small"
                        label={pmt.is_active ? t('Active') : t('Inactive')}
                        color={pmt.is_active ? 'success' : 'default'}
                        variant={pmt.is_active ? 'filled' : 'outlined'}
                    />
                ) : (
                    <Tooltip title={pmt.is_active ? t('Click to deactivate') : t('Click to activate')}>
                        <Chip
                            size="small"
                            label={pmt.is_active ? t('Active') : t('Inactive')}
                            color={pmt.is_active ? 'success' : 'default'}
                            variant={pmt.is_active ? 'filled' : 'outlined'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleStatus(pmt);
                            }}
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                        />
                    </Tooltip>
                )}
            </TableCell>
            <TableCell sx={{ fontWeight: 500 }}>
                {pmt.description}
                {pmt.category && (
                    <Chip size="small" label={t(pmt.category.name)} sx={{ ml: 1, fontSize: '0.7rem', height: 20 }} />
                )}
            </TableCell>
            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    {pmt.action === ScheduledTransactionAction.MOVE
                        ? `${pmt.from_account ? `${t(pmt.from_account.account_type)} (${pmt.from_account.currency})` : '??'} → ${pmt.to_account ? `${t(pmt.to_account.account_type)} (${pmt.to_account.currency})` : '??'}`
                        : pmt.action === ScheduledTransactionAction.INFLOW
                            ? (pmt.to_account ? `${t(pmt.to_account.account_type)} (${pmt.to_account.currency})` : pmt.currency)
                            : (pmt.from_account ? `${t(pmt.from_account.account_type)} (${pmt.from_account.currency})` : pmt.currency)
                    }
                </Typography>
                {pmt.action === ScheduledTransactionAction.MOVE && pmt.next_run_date && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', opacity: 0.7, mt: 0.5 }}>
                        ({t('next run')}: {formatDateSafe(pmt.next_run_date, i18n.language)})
                    </Typography>
                )}
            </TableCell>
            <TableCell>
                <Chip
                    size="small"
                    icon={pmt.payment_type === 'RECURRING' ? <RepeatIcon /> : <EventIcon />}
                    label={pmt.payment_type === 'RECURRING' ? t('Recurring') : t('One-time')}
                    variant="outlined"
                />
            </TableCell>
            <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                {pmt.payment_type === 'RECURRING' ? (
                    INTERVAL_LABELS[pmt.interval] ? t(INTERVAL_LABELS[pmt.interval]) : pmt.interval
                ) : (
                    formatDateSafe(pmt.start_date, i18n.language)
                )}
            </TableCell>
            <TableCell align="right">
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        color: pmt.action === ScheduledTransactionAction.MOVE ? 'text.primary' : (Number(pmt.amount) >= 0 ? 'success.main' : 'error.main')
                    }}
                >
                    {pmt.action !== ScheduledTransactionAction.MOVE && (Number(pmt.amount) >= 0 ? '+' : '')}
                    {formatCurrency(pmt.action === ScheduledTransactionAction.MOVE ? Math.abs(pmt.amount) : pmt.amount, pmt.currency)}
                    <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem', opacity: 0.7 }}>
                        {pmt.currency}
                    </Box>
                </Typography>
            </TableCell>
            {!isViewer && (
                <TableCell align="right">
                    <IconButton
                        size="small"
                        aria-label={`payment-actions-${pmt.id}`}
                        onClick={e => onOpenMenu(e, pmt.id)}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </TableCell>
            )}
        </TableRow>
    );
});

// Memoized card for mobile view of Account Balances
const AccountBalanceCard = React.memo(({
    stat,
    acc,
    isSelected,
    onToggleSelect,
    onSelectOnly,
    onUpdateYearlyBalance,
    onUpdateAccountStartAmount,
    onOpenContributionDialog,
    formatCurrency,
    forecastCurrency,
    isViewer,
    isStartingBalanceLocked,
    onToggleStartingBalanceLock,
    accountAsOfDate,
    onOpenDatePopover
}) => {
    const { t, i18n } = useTranslation();
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 1.5,
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                borderRadius: 1,
                opacity: isSelected ? 1 : 0.6,
                bgcolor: isSelected ? 'background.paper' : 'action.hover'
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={(e) => onToggleSelect(stat.account_id, e.target.checked)}
                        sx={{ p: 0 }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {formatAccountDisplayName(stat, acc, t)}
                    </Typography>
                </Box>
                <Button
                    size="small"
                    onClick={() => onSelectOnly(stat.account_id)}
                    sx={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        px: 1,
                        py: '2px',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        lineHeight: 1.2,
                        textTransform: 'none',
                        color: 'primary.main',
                        bgcolor: 'primary.50',
                        border: '1px solid',
                        borderColor: 'primary.200',
                        borderRadius: '8px',
                        minWidth: 'auto',
                        '&:hover': {
                            bgcolor: 'primary.100',
                            borderColor: 'primary.300',
                        }
                    }}
                >
                    {t('Select Only')}
                </Button>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Stack spacing={1} sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('Starting Balance')} ({stat.currency}):
                        </Typography>
                        <Tooltip title={isStartingBalanceLocked ? t('Unlock to edit starting balance') : t('Lock starting balance')}>
                            <IconButton
                                size="small"
                                onClick={onToggleStartingBalanceLock}
                                disabled={isViewer}
                                sx={{
                                    p: 0.25,
                                    color: isStartingBalanceLocked ? 'text.secondary' : 'primary.main'
                                }}
                                aria-label={isStartingBalanceLocked ? 'unlock-starting-balance' : 'lock-starting-balance'}
                            >
                                {isStartingBalanceLocked ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <BalanceInput
                        disabled={!isSelected || isViewer || isStartingBalanceLocked}
                        initialValue={stat.starting_balance}
                        onSave={(num) => onUpdateYearlyBalance(stat.account_id, num)}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('Current Balance')} ({stat.currency}):
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                            <Button
                                size="small"
                                onClick={(e) => onOpenDatePopover(e, stat.account_id)}
                                disabled={!isSelected || isViewer}
                                sx={{
                                    p: '0px 4px',
                                    fontSize: '0.65rem',
                                    textTransform: 'none',
                                    color: 'text.secondary',
                                    borderRadius: 1,
                                    minWidth: 0,
                                    lineHeight: 1.2,
                                    '&:hover': { bgcolor: 'action.hover', color: 'primary.main' }
                                }}
                                startIcon={<CalendarTodayIcon sx={{ fontSize: '0.7rem !important' }} />}
                            >
                                {t('As of')} {formatDateSafe(accountAsOfDate, i18n.language)}
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <BalanceInput
                            disabled={!isSelected || isViewer}
                            initialValue={acc ? acc.start_amount : 0}
                            onSave={(num) => onUpdateAccountStartAmount(stat.account_id, num)}
                        />
                        {acc?.account_type === AccountType.INVESTMENT && (
                            <Tooltip title={t('Add investment contribution')}>
                                <IconButton
                                    size="small"
                                    disabled={!isSelected || isViewer}
                                    onClick={() => onOpenContributionDialog && onOpenContributionDialog(acc, acc?.start_amount || 0)}
                                    sx={{
                                        position: 'absolute',
                                        left: '100%',
                                        ml: 0.5,
                                        p: 0.25,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        color: 'primary.main',
                                        bgcolor: 'background.paper',
                                        '&:hover': {
                                            bgcolor: 'primary.50',
                                            borderColor: 'primary.main'
                                        }
                                    }}
                                    aria-label="add-investment-contribution"
                                >
                                    <AddIcon sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {t('Year-End Balance')}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(stat.end_of_year_balance, stat.currency)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        {t('Annual Savings')}:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                color: stat.difference >= 0 ? 'success.main' : 'error.main'
                            }}
                        >
                            {stat.difference > 0 ? '+' : ''}{formatCurrency(stat.difference, stat.currency)} {stat.currency}
                        </Typography>
                        {stat.currency !== forecastCurrency && (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
                                ≈ {stat.difference_preferred > 0 ? '+' : ''}{formatCurrency(stat.difference_preferred, forecastCurrency)} {forecastCurrency}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Stack>
        </Paper>
    );
});

// Memoized card for mobile view of Scheduled Payments
const ScheduledPaymentCard = React.memo(({ pmt, formatDateSafe, formatCurrency, onToggleStatus, onOpenMenu, isViewer }) => {
    const { t, i18n } = useTranslation();
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                opacity: pmt.is_active ? 1 : 0.6
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        size="small"
                        label={pmt.is_active ? t('Active') : t('Inactive')}
                        color={pmt.is_active ? 'success' : 'default'}
                        variant={pmt.is_active ? 'filled' : 'outlined'}
                        onClick={!isViewer ? (e) => { e.stopPropagation(); onToggleStatus(pmt); } : undefined}
                        sx={{ height: 22, fontSize: '0.7rem', cursor: isViewer ? 'default' : 'pointer' }}
                    />
                    <Chip
                        size="small"
                        icon={pmt.payment_type === 'RECURRING' ? <RepeatIcon /> : <EventIcon />}
                        label={pmt.payment_type === 'RECURRING' ? (INTERVAL_LABELS[pmt.interval] ? t(INTERVAL_LABELS[pmt.interval]) : pmt.interval) : formatDateSafe(pmt.start_date, i18n.language)}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                </Box>
                {!isViewer && (
                    <IconButton
                        size="small"
                        aria-label={`payment-actions-${pmt.id}`}
                        onClick={e => onOpenMenu(e, pmt.id)}
                        sx={{ mt: -0.5, mr: -0.5 }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {pmt.description}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 500 }}>
                        {pmt.action === ScheduledTransactionAction.MOVE
                            ? `${pmt.from_account ? `${t(pmt.from_account.account_type)} (${pmt.from_account.currency})` : '??'} → ${pmt.to_account ? `${t(pmt.to_account.account_type)} (${pmt.to_account.currency})` : '??'}`
                            : pmt.action === ScheduledTransactionAction.INFLOW
                                ? (pmt.to_account ? `${t(pmt.to_account.account_type)} (${pmt.to_account.currency})` : pmt.currency)
                                : (pmt.from_account ? `${t(pmt.from_account.account_type)} (${pmt.from_account.currency})` : pmt.currency)
                        }
                    </Typography>
                    {pmt.category && (
                        <Chip size="small" label={t(pmt.category.name)} sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }} />
                    )}
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: pmt.action === ScheduledTransactionAction.MOVE ? 'text.primary' : (Number(pmt.amount) >= 0 ? 'success.main' : 'error.main')
                    }}
                >
                    {pmt.action !== ScheduledTransactionAction.MOVE && (Number(pmt.amount) >= 0 ? '+' : '')}
                    {formatCurrency(pmt.action === ScheduledTransactionAction.MOVE ? Math.abs(pmt.amount) : pmt.amount, pmt.currency)} {pmt.currency}
                </Typography>
            </Box>
        </Paper>
    );
});

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
export default function CashFlow() {
    const { t, i18n } = useTranslation();
    const { id: ledgerId } = useParams();
    const navigate = useNavigate();
    const { api, formatCurrency, preferredCurrency, preferredTimezone } = useAuth();
    const { showToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // ── Forecast State ──
    const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
    const [availableYears, setAvailableYears] = useState([CURRENT_YEAR]);
    const [forecast, setForecast] = useState(null);
    const [forecastLoading, setForecastLoading] = useState(true);
    const [forecastError, setForecastError] = useState(null);
    const [isStartingBalanceLocked, setIsStartingBalanceLocked] = useState(true);
    const [chartRange, setChartRange] = useState(CashFlowRange.THREE_MONTHS);

    // ── Scheduled Payments State ──
    const [payments, setPayments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);
    const [userRole, setUserRole] = useState('viewer');
    const [isLoading, setIsLoading] = useState(true);

    // ── Multi-Account State ──
    const [accounts, setAccounts] = useState([]);
    const [selectedAccountIds, setSelectedAccountIds] = useState([]);
    const [accountManagementOpen, setAccountManagementOpen] = useState(false);
    const [accountFormOpen, setAccountFormOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [accountFormData, setAccountFormData] = useState({ account_type: AccountType.CHECKING, currency: 'CAD' });
    const [accountToDelete, setAccountToDelete] = useState(null);

    // ── Balance As Of State ──
    const [asOfDateAnchorEl, setAsOfDateAnchorEl] = useState(null);
    const [activeAsOfAccountId, setActiveAsOfAccountId] = useState(null);
    const [tempAsOfDate, setTempAsOfDate] = useState('');

    // ── Investment Contribution Dialog State ──
    const [contributionDialogOpen, setContributionDialogOpen] = useState(false);
    const [contributionAccount, setContributionAccount] = useState(null);
    const [contributionCurrentBalance, setContributionCurrentBalance] = useState(0);

    const handleOpenContributionDialog = (account, currentBalance) => {
        setContributionAccount(account);
        setContributionCurrentBalance(currentBalance);
        setContributionDialogOpen(true);
    };

    const handleCloseContributionDialog = () => {
        setContributionDialogOpen(false);
        setContributionAccount(null);
    };

    const handleOpenDatePopover = (event, accountId) => {
        const todayStr = getTodayInTimezone(preferredTimezone);
        const acc = accounts.find(a => a.id === accountId);
        const stat = forecast?.yearly_stats?.find(s => s.account_id === accountId);
        const currentAsOf = stat?.balance_as_of || acc?.balance_as_of || acc?.created_at?.split('T')[0] || todayStr;
        setActiveAsOfAccountId(accountId);
        setTempAsOfDate(currentAsOf);
        setAsOfDateAnchorEl(event.currentTarget);
    };

    const handleCloseDatePopover = () => {
        setAsOfDateAnchorEl(null);
        setActiveAsOfAccountId(null);
    };

    const handleSaveAsOfDate = async (dateVal) => {
        if (activeAsOfAccountId) {
            try {
                await api.put(`/ledger/ledgers/${ledgerId}/accounts/${activeAsOfAccountId}`, {
                    balance_as_of: dateVal || null
                });
                showToast(t('Statement date updated.'), 'success');
                await Promise.all([fetchAccounts(), fetchForecast()]);
            } catch (err) {
                console.error('Failed to update statement date', err.response?.data || err.message);
                showToast(`Error: ${err.response?.data?.detail || t('Failed to update statement date.')}`, 'error');
            }
        }
        handleCloseDatePopover();
    };

    // ── Dialog State ──
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentPayment, setCurrentPayment] = useState(null);
    const [formData, setFormData] = useState({ ...EMPTY_FORM, currency: preferredCurrency || 'CAD' });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [isAccountSubmitting, setIsAccountSubmitting] = useState(false);

    // ── Copy Recurring State ──
    const [copyRecurringOpen, setCopyRecurringOpen] = useState(false);
    const [recurringRules, setRecurringRules] = useState([]);
    const [recurringRulesLoading, setRecurringRulesLoading] = useState(false);
    const [selectedRuleIds, setSelectedRuleIds] = useState([]);
    const [isImporting, setIsImporting] = useState(false);

    // ── Menu State ──
    const [anchorEl, setAnchorEl] = useState(null);

    // ── Copy Recurring Functions ──
    const isAlreadyImported = useCallback((rule) => {
        if (!payments || payments.length === 0) return false;
        const ruleAbsAmount = Math.abs(Number(rule.amount));
        const ruleStartDateStr = (rule.start_date || '').split('T')[0];

        return payments.some(pmt => {
            const pmtAbsAmount = Math.abs(Number(pmt.amount));
            const pmtStartDateStr = (pmt.start_date || '').split('T')[0];

            return pmt.payment_type === 'RECURRING' &&
                pmt.description?.toLowerCase() === rule.description?.toLowerCase() &&
                pmtAbsAmount === ruleAbsAmount &&
                pmt.currency === rule.currency &&
                pmt.interval === rule.interval &&
                pmtStartDateStr === ruleStartDateStr;
        });
    }, [payments]);

    const handleOpenCopyRecurringDialog = async () => {
        setCopyRecurringOpen(true);
        setRecurringRulesLoading(true);
        setSelectedRuleIds([]);
        try {
            const res = await api.get(`/ledger/ledgers/${ledgerId}/recurring-transactions/`);
            setRecurringRules(res.data);
        } catch (err) {
            console.error('Failed to fetch recurring rules', err);
            showToast(t('Failed to load recurring rules.'), 'error');
        } finally {
            setRecurringRulesLoading(false);
        }
    };

    const handleCopyRecurringSubmit = async () => {
        const rulesToCopy = recurringRules.filter(r => selectedRuleIds.includes(r.id));
        if (rulesToCopy.length === 0) return;

        setIsImporting(true);
        try {
            await Promise.all(rulesToCopy.map(async (rule) => {
                const isCredit = Number(rule.amount) < 0;
                const absAmount = Math.abs(Number(rule.amount));

                const payload = {
                    description: rule.description,
                    amount: isCredit ? absAmount : -absAmount,
                    currency: rule.currency,
                    payment_type: 'RECURRING',
                    interval: rule.interval,
                    interval_config: rule.interval_config,
                    start_date: rule.start_date,
                    end_date: rule.end_date,
                    is_active: rule.is_active,
                    category_id: rule.category_id,
                    action: isCredit ? 'INFLOW' : 'OUTFLOW',
                    from_account_id: isCredit ? null : rule.account_id,
                    to_account_id: isCredit ? rule.account_id : null
                };

                return api.post(`/ledger/ledgers/${ledgerId}/scheduled-payments/`, payload);
            }));

            showToast(t('Successfully imported recurring rules to forecast!'), 'success');
            setCopyRecurringOpen(false);
            await Promise.all([fetchPayments(), fetchForecast()]);
        } catch (err) {
            console.error('Failed to import recurring rules', err);
            showToast(err.response?.data?.detail || t('Failed to import selected recurring rules.'), 'error');
        } finally {
            setIsImporting(false);
        }
    };

    // ─────────────────────────────────────────────
    // Data fetching
    // ─────────────────────────────────────────────
    const fetchForecast = useCallback(async () => {
        setForecastLoading(true);
        setForecastError(null);
        try {
            const queryAccountParams = selectedAccountIds.length > 0
                ? `&${selectedAccountIds.map(id => `account_ids=${id}`).join('&')}`
                : '';
            const res = await api.get(
                `/ledger/ledgers/${ledgerId}/cashflow-forecast/?year=${selectedYear}${queryAccountParams}`
            );
            setForecast(res.data);
            if (res.data.available_years) {
                setAvailableYears(res.data.available_years);
            }
        } catch (err) {
            console.error('Failed to fetch cash flow forecast', err);
            setForecastError(t('Failed to load cash flow forecast.'));
        } finally {
            setForecastLoading(false);
        }
    }, [api, ledgerId, selectedYear, selectedAccountIds, t]);

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await api.get(`/ledger/ledgers/${ledgerId}/accounts`);
            const sorted = sortAccounts(res.data, preferredCurrency);
            setAccounts(sorted);
            if (sorted.length > 0 && selectedAccountIds.length === 0) {
                setSelectedAccountIds(sorted.map(a => a.id));
            }
            return sorted;
        } catch (err) {
            console.error('Failed to fetch accounts', err);
            return [];
        }
    }, [api, ledgerId, selectedAccountIds.length, preferredCurrency]);

    const fetchPayments = useCallback(async () => {
        setIsLoading(true);
        try {
            const [pmtRes, catRes, ledgerRes] = await Promise.all([
                api.get(`/ledger/ledgers/${ledgerId}/scheduled-payments/`).catch(() => ({ data: [] })),
                api.get(`/categories/ledger/${ledgerId}`).catch(() => ({ data: [] })),
                api.get('/ledger/ledgers').catch(() => ({ data: [] })),
                fetchAccounts()
            ]);

            setPayments(pmtRes.data);
            setCategories(catRes.data);

            const currentLedger = ledgerRes.data.find(l => l.id?.toString() === ledgerId?.toString());
            if (currentLedger) {
                setUserRole(currentLedger.role || 'viewer');
            }
        } catch (err) {
            console.error('Global error in fetchPayments', err);
        } finally {
            setIsLoading(false);
        }
    }, [api, ledgerId, fetchAccounts]);

    const handleToggleAccountSelect = useCallback((accId, checked) => {
        if (checked) {
            setSelectedAccountIds(prev => [...prev, accId]);
        } else {
            setSelectedAccountIds(prev => prev.length > 1 ? prev.filter(id => id !== accId) : prev);
        }
    }, []);

    const handleSelectAccountOnly = useCallback((accId) => {
        setSelectedAccountIds([accId]);
    }, []);

    const handleOpenPaymentMenu = useCallback((event, paymentId) => {
        setAnchorEl(event.currentTarget);
        setSelectedPaymentId(paymentId);
    }, []);

    useEffect(() => { fetchPayments(); }, [fetchPayments]);
    useEffect(() => { fetchForecast(); }, [fetchForecast]);

    // ─────────────────────────────────────────────
    // Chart data transformation with timeframe range filter
    // ─────────────────────────────────────────────
    const filteredDataPoints = useMemo(() => {
        if (!forecast?.data_points || forecast.data_points.length === 0) return [];
        
        const startStr = forecast.forecast_start_date || forecast.data_points[0].week_start;
        const startDate = new Date(startStr + 'T00:00:00');
        
        const cutoffDate = new Date(startDate);
        if (chartRange === CashFlowRange.ONE_MONTH) {
            cutoffDate.setMonth(cutoffDate.getMonth() + 1);
        } else if (chartRange === CashFlowRange.THREE_MONTHS) {
            cutoffDate.setMonth(cutoffDate.getMonth() + 3);
        } else if (chartRange === CashFlowRange.SIX_MONTHS) {
            cutoffDate.setMonth(cutoffDate.getMonth() + 6);
        } else if (chartRange === CashFlowRange.ONE_YEAR) {
            cutoffDate.setFullYear(cutoffDate.getFullYear() + 1);
        }
        
        const cutoffStr = cutoffDate.toISOString().split('T')[0];
        return forecast.data_points.filter(dp => dp.week_start <= cutoffStr);
    }, [forecast, chartRange]);

    const chartData = useMemo(() => {
        if (!filteredDataPoints || filteredDataPoints.length === 0) return { labels: [], credits: [], debits: [], balance: [] };
        const dp = filteredDataPoints;
        const labels = dp.map(d => d.week_start); // Keep ISO strings (YYYY-MM-DD)
        return {
            labels,
            credits: dp.map(d => parseFloat(d.credits)),
            debits: dp.map(d => parseFloat(d.debits)),
            netPositive: dp.map(d => {
                const net = parseFloat(d.credits) - parseFloat(d.debits);
                return net >= 0 ? net : 0;
            }),
            netNegative: dp.map(d => {
                const net = parseFloat(d.credits) - parseFloat(d.debits);
                return net < 0 ? net : 0;
            }),
            balance: dp.map(d => parseFloat(d.running_balance)),
        };
    }, [filteredDataPoints]);

    const filteredDailyDataPoints = useMemo(() => {
        const dailyPoints = forecast?.daily_data_points;
        if (!dailyPoints || dailyPoints.length === 0) return [];

        const startStr = forecast.forecast_start_date || dailyPoints[0].date;
        const startDate = new Date(startStr + 'T00:00:00');

        const cutoffDate = new Date(startDate);
        if (chartRange === CashFlowRange.ONE_MONTH) {
            cutoffDate.setMonth(cutoffDate.getMonth() + 1);
        } else if (chartRange === CashFlowRange.THREE_MONTHS) {
            cutoffDate.setMonth(cutoffDate.getMonth() + 3);
        } else if (chartRange === CashFlowRange.SIX_MONTHS) {
            cutoffDate.setMonth(cutoffDate.getMonth() + 6);
        } else if (chartRange === CashFlowRange.ONE_YEAR) {
            cutoffDate.setFullYear(cutoffDate.getFullYear() + 1);
        }

        const cutoffStr = cutoffDate.toISOString().split('T')[0];
        return dailyPoints.filter(dp => dp.date <= cutoffStr);
    }, [forecast, chartRange]);

    const dailyChartData = useMemo(() => {
        if (!filteredDailyDataPoints || filteredDailyDataPoints.length === 0) {
            return { labels: [], balance: [] };
        }
        return {
            labels: filteredDailyDataPoints.map(d => d.date),
            balance: filteredDailyDataPoints.map(d => parseFloat(d.running_balance)),
        };
    }, [filteredDailyDataPoints]);



    // ─────────────────────────────────────────────
    // Form helpers
    // ─────────────────────────────────────────────
    const formatCurrencyInput = (value) => {
        if (!value) return '';
        let val = value.toString().replace(/[^0-9.]/g, '');
        const parts = val.split('.');
        let cleanVal = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : val;
        if (!cleanVal) return '';
        let [intPart, decPart] = cleanVal.split('.');
        if (intPart === '') intPart = '0';
        if (intPart.length > 1 && intPart.startsWith('0')) intPart = parseInt(intPart, 10).toString();
        const formattedInt = new Intl.NumberFormat('en-US').format(intPart);
        if (decPart !== undefined) { decPart = decPart.slice(0, 2); return `${formattedInt}.${decPart}`; }
        return formattedInt;
    };

    const resetForm = useCallback(() => {
        setCurrentPayment(null);
        const defCurrency = preferredCurrency || 'CAD';
        const matchingAccounts = accounts.filter(acc => acc.currency === defCurrency);
        const defaultAccount = matchingAccounts.length > 0 ? matchingAccounts[0].id : '';
        setFormData({
            ...EMPTY_FORM,
            currency: defCurrency,
            start_date: getTodayInTimezone(preferredTimezone),
            from_account_id: defaultAccount,
            to_account_id: defaultAccount,
        });
    }, [preferredCurrency, preferredTimezone, accounts]);

    const handleOpenAdd = () => { resetForm(); setDialogOpen(true); };

    const handleOpenEdit = (pmt) => {
        setCurrentPayment(pmt);
        setFormData({
            description: pmt.description,
            amount: Math.abs(Number(pmt.amount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            currency: pmt.currency || preferredCurrency || 'CAD',
            payment_type: pmt.payment_type,
            flow_direction: pmt.action === ScheduledTransactionAction.INFLOW ? 'credit' : 'debit',
            interval: pmt.interval || RecurringInterval.MONTHLY,
            interval_config: pmt.interval_config || null,
            start_date: (pmt.start_date || '').split('T')[0],
            end_date: pmt.end_date ? pmt.end_date.split('T')[0] : '',
            is_active: pmt.is_active,
            category_id: pmt.category_id || '',
            action: pmt.action || (Number(pmt.amount) >= 0 ? ScheduledTransactionAction.INFLOW : ScheduledTransactionAction.OUTFLOW),
            from_account_id: pmt.from_account_id || '',
            to_account_id: pmt.to_account_id || '',
        });
        setAnchorEl(null);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.description.trim()) { showToast(t('Description is required.'), 'error'); return; }
        const parsedAmount = parseFloat((formData.amount || '').toString().replace(/,/g, ''));
        if (!parsedAmount || isNaN(parsedAmount) || parsedAmount <= 0) {
            showToast(t('Amount must be greater than zero.'), 'error'); return;
        }
        // Mirror backend: credit = positive, debit = negative
        const finalAmount = formData.action === ScheduledTransactionAction.INFLOW ? Math.abs(parsedAmount) : -Math.abs(parsedAmount);

        if (formData.payment_type === ScheduledTransactionType.RECURRING) {
            if (!formData.interval) { showToast(t('Please select an interval.'), 'error'); return; }
            if (formData.interval === RecurringInterval.DAYS_OF_WEEK && !formData.interval_config?.days_of_week?.length) {
                showToast(t('Select at least one day of the week.'), 'error'); return;
            }
            if (formData.interval === RecurringInterval.DAYS_OF_MONTH && !formData.interval_config?.days_of_month?.length) {
                showToast(t('Select at least one day of the month.'), 'error'); return;
            }
        }

        const payload = {
            description: formData.description.trim(),
            amount: finalAmount,
            currency: formData.currency,
            payment_type: formData.payment_type,
            interval: formData.payment_type === ScheduledTransactionType.RECURRING ? formData.interval : null,
            interval_config: formData.payment_type === ScheduledTransactionType.RECURRING ? formData.interval_config : null,
            start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
            end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
            is_active: formData.is_active,
            category_id: formData.category_id || null,
            action: formData.action,
            from_account_id: (formData.action === ScheduledTransactionAction.OUTFLOW || formData.action === ScheduledTransactionAction.MOVE) ? (formData.from_account_id || null) : null,
            to_account_id: (formData.action === ScheduledTransactionAction.INFLOW || formData.action === ScheduledTransactionAction.MOVE) ? (formData.to_account_id || null) : null,
        };

        setIsSaving(true);
        try {
            if (currentPayment) {
                await api.put(`/ledger/ledgers/${ledgerId}/scheduled-payments/${currentPayment.id}`, payload);
                showToast(t('Transaction updated.'), 'success');
            } else {
                await api.post(`/ledger/ledgers/${ledgerId}/scheduled-payments/`, payload);
                showToast(t('Transaction added.'), 'success');
            }
            setDialogOpen(false);
            await Promise.all([fetchPayments(), fetchForecast()]);
        } catch (err) {
            console.error('Failed to save transaction', err);
            showToast(err.response?.data?.detail || t('Failed to save transaction.'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedPaymentId) return;
        setIsDeleting(true);
        try {
            await api.delete(`/ledger/ledgers/${ledgerId}/scheduled-payments/${selectedPaymentId}`);
            showToast(t('Transaction removed.'), 'success');
            setDeleteDialogOpen(false);
            setSelectedPaymentId(null);
            await Promise.all([fetchPayments(), fetchForecast()]);
        } catch (err) {
            console.error('Failed to delete transaction', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleStatus = async (pmt) => {
        try {
            // Partial update using the existing PUT endpoint (requires full payload usually)
            const payload = {
                description: pmt.description,
                amount: pmt.amount,
                currency: pmt.currency,
                payment_type: pmt.payment_type,
                interval: pmt.interval,
                interval_config: pmt.interval_config,
                start_date: pmt.start_date,
                end_date: pmt.end_date,
                is_active: !pmt.is_active,
                category_id: pmt.category_id,
                action: pmt.action,
                from_account_id: pmt.from_account_id,
                to_account_id: pmt.to_account_id,
            };

            await api.put(`/ledger/ledgers/${ledgerId}/scheduled-payments/${pmt.id}`, payload);
            showToast(t('Transaction updated.'), 'success');
            await Promise.all([fetchPayments(), fetchForecast()]);
        } catch (err) {
            console.error('Failed to toggle status', err);
            showToast(t('Failed to update status.'), 'error');
        }
    };


    const handleSaveAccount = async () => {
        const payload = {
            account_type: accountFormData.account_type,
            currency: accountFormData.currency,
        };
        if (!editingAccount) {
            payload.start_amount = 0;
        }

        setIsAccountSubmitting(true);
        try {
            if (editingAccount) {
                await api.put(`/ledger/ledgers/${ledgerId}/accounts/${editingAccount.id}`, payload);
                showToast(t('Account updated.'), 'success');
            } else {
                const addRes = await api.post(`/ledger/ledgers/${ledgerId}/accounts`, payload);
                showToast(t('Account created.'), 'success');
                setSelectedAccountIds(prev => {
                    if (!prev.includes(addRes.data.id)) return [...prev, addRes.data.id];
                    return prev;
                });
            }
            setEditingAccount(null);
            setAccountFormOpen(false);
            setAccountFormData({ account_type: AccountType.CHECKING, currency: 'CAD' });
            await fetchAccounts();
            await fetchForecast();
        } catch (err) {
            console.error('Failed to save account', err);
            showToast(err.response?.data?.detail || t('Failed to save account.'), 'error');
        } finally {
            setIsAccountSubmitting(false);
        }
    };

    const handleDeleteAccount = (accId) => {
        setAccountToDelete(accId);
    };

    const handleDeleteAccountConfirm = async () => {
        if (!accountToDelete) return;
        setIsDeletingAccount(true);
        try {
            await api.delete(`/ledger/ledgers/${ledgerId}/accounts/${accountToDelete}`);
            showToast(t('Account deleted.'), 'success');

            // Remove from selected account list
            setSelectedAccountIds(prev => prev.filter(id => id !== accountToDelete));

            setAccountToDelete(null);
            await fetchAccounts();
            await fetchForecast();
        } catch (err) {
            console.error('Failed to delete account', err);
            showToast(err.response?.data?.detail || t('Failed to delete account.'), 'error');
            setAccountToDelete(null);
        } finally {
            setIsDeletingAccount(false);
        }
    };

    const handleUpdateYearlyBalance = async (accountId, balance) => {
        try {
            await api.put(`/ledger/ledgers/${ledgerId}/accounts/${accountId}/yearly-balances/${selectedYear}`, {
                balance: parseFloat(balance) || 0
            });
            showToast(t('Starting balance updated.'), 'success');
            fetchForecast();
        } catch (err) {
            console.error('Failed to update yearly balance', err.response?.data || err.message);
            showToast(`Error: ${err.response?.data?.detail || t('Failed to update balance.')}`, 'error');
        }
    };

    const handleUpdateAccountStartAmount = async (accountId, balance) => {
        const acc = accounts.find(a => a.id === accountId);
        const newBal = parseFloat(balance) || 0;
        const currentBal = (acc && acc.start_amount !== undefined && acc.start_amount !== null) ? (parseFloat(acc.start_amount) || 0) : 0;

        if (Math.abs(newBal - currentBal) < 0.0001) {
            return;
        }

        const todayStr = getTodayInTimezone(preferredTimezone);
        try {
            await api.put(`/ledger/ledgers/${ledgerId}/accounts/${accountId}`, {
                start_amount: newBal,
                balance_as_of: todayStr
            });
            showToast(t('Account balance updated.'), 'success');
            await Promise.all([fetchAccounts(), fetchForecast()]);
        } catch (err) {
            console.error('Failed to update account balance', err.response?.data || err.message);
            showToast(`Error: ${err.response?.data?.detail || t('Failed to update balance.')}`, 'error');
        }
    };

    const hasChartData = chartData.credits.some(v => v > 0) || chartData.debits.some(v => v > 0);

    // ─────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────
    return (
        <Container maxWidth="lg" sx={{ mt: 6, mb: 12 }}>
            {/* ── Header ── */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 4, gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                {t('Cash Flow Forecast')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>

                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel id="year-label">{t('Year')}</InputLabel>
                        <Select
                            labelId="year-label"
                            value={selectedYear}
                            label={t('Year')}
                            onChange={e => setSelectedYear(e.target.value)}
                            sx={{ bgcolor: 'background.paper' }}
                        >
                            {availableYears.map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <LoadingOverlay open={isLoading} />

            {forecastError && <Alert severity="error" sx={{ mb: 3 }}>{forecastError}</Alert>}

            {/* ── Unified Account Balances & Forecast Table ── */}
            {accounts.length > 0 && forecast?.yearly_stats && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                            {t('ACCOUNT BALANCES')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button
                                size="small"
                                variant={selectedAccountIds.length === accounts.length ? "contained" : "outlined"}
                                onClick={() => setSelectedAccountIds(accounts.map(a => a.id))}
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: 1.5
                                }}
                            >
                                {t('Select All')}
                            </Button>
                            {userRole !== 'viewer' && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<SettingsIcon />}
                                    onClick={() => setAccountManagementOpen(true)}
                                    sx={{
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 1.5
                                    }}
                                >
                                    {t('Manage Accounts')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                    {isMobile ? (
                        <Box sx={{ mb: 4 }}>
                            {forecast.yearly_stats.map((stat) => {
                                const isSelected = selectedAccountIds.includes(stat.account_id);
                                const acc = accounts.find(a => a.id === stat.account_id);
                                return (
                                    <AccountBalanceCard
                                        key={stat.account_id}
                                        stat={stat}
                                        acc={acc}
                                        isSelected={isSelected}
                                        onToggleSelect={handleToggleAccountSelect}
                                        onSelectOnly={handleSelectAccountOnly}
                                        onUpdateYearlyBalance={handleUpdateYearlyBalance}
                                        onUpdateAccountStartAmount={handleUpdateAccountStartAmount}
                                        onOpenContributionDialog={handleOpenContributionDialog}
                                        formatCurrency={formatCurrency}
                                        forecastCurrency={forecast.currency}
                                        isViewer={userRole === 'viewer'}
                                        isStartingBalanceLocked={isStartingBalanceLocked}
                                        onToggleStartingBalanceLock={() => setIsStartingBalanceLocked(!isStartingBalanceLocked)}
                                        accountAsOfDate={stat.balance_as_of || (acc && acc.balance_as_of) || acc?.created_at?.split('T')[0] || getTodayInTimezone(preferredTimezone)}
                                        onOpenDatePopover={handleOpenDatePopover}
                                    />
                                );
                            })}
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('TOTAL SAVED / DEFICIT')}</Typography>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: parseFloat(forecast.total_annual_savings) >= 0 ? 'success.main' : 'error.main' }}>
                                        {formatCurrency(forecast.total_annual_savings, forecast.currency)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: -0.5, opacity: 0.7, fontWeight: 500 }}>
                                        {forecast.currency} {t('TOTAL')}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={0} sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                        }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell sx={{ fontWeight: 700, py: 1.5 }}>{t('ACCOUNT')}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                <span>{t('STARTING BALANCE')}</span>
                                                <Tooltip title={isStartingBalanceLocked ? t('Unlock to edit starting balance') : t('Lock starting balance')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setIsStartingBalanceLocked(prev => !prev)}
                                                        disabled={userRole === 'viewer'}
                                                        sx={{
                                                            p: 0.5,
                                                            color: isStartingBalanceLocked ? 'text.secondary' : 'primary.main',
                                                            '&:hover': { bgcolor: 'action.hover' }
                                                        }}
                                                        aria-label={isStartingBalanceLocked ? 'unlock-starting-balance' : 'lock-starting-balance'}
                                                    >
                                                        {isStartingBalanceLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                <span>{t('BALANCE AS OF')}</span>
                                                <Tooltip title={t('Account balance and statement reconciliation date')}>
                                                    <InfoOutlinedIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{t('YEAR-END BALANCE')}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{t('ANNUAL SAVINGS')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {forecast.yearly_stats.map((stat) => {
                                        const isSelected = selectedAccountIds.includes(stat.account_id);
                                        const acc = accounts.find(a => a.id === stat.account_id);
                                        return (
                                            <TableRow
                                                key={stat.account_id}
                                                hover
                                                sx={{
                                                    '& .only-btn': { opacity: 0, transition: 'opacity 0.2s' },
                                                    '&:hover .only-btn': { opacity: 1 }
                                                }}
                                            >
                                                <TableCell padding="checkbox" sx={{ opacity: isSelected ? 1 : 0.4 }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={isSelected}
                                                        onChange={(e) => handleToggleAccountSelect(stat.account_id, e.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                                                        <Typography component="span" sx={{ fontWeight: 500, opacity: isSelected ? 1 : 0.4, whiteSpace: 'nowrap' }}>
                                                            {formatAccountDisplayName(stat, acc, t)}
                                                        </Typography>
                                                        <Button
                                                            className="only-btn"
                                                            size="small"
                                                            onClick={() => handleSelectAccountOnly(stat.account_id)}
                                                            sx={{
                                                                flexShrink: 0,
                                                                whiteSpace: 'nowrap',
                                                                px: 1,
                                                                py: '2px',
                                                                fontSize: '0.68rem',
                                                                fontWeight: 600,
                                                                lineHeight: 1.2,
                                                                textTransform: 'none',
                                                                color: 'primary.main',
                                                                bgcolor: 'primary.50',
                                                                border: '1px solid',
                                                                borderColor: 'primary.200',
                                                                borderRadius: '8px',
                                                                minWidth: 'auto',
                                                                '&:hover': {
                                                                    bgcolor: 'primary.100',
                                                                    borderColor: 'primary.300',
                                                                }
                                                            }}
                                                        >
                                                            {t('Select Only')}
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ opacity: isSelected ? 1 : 0.4 }}>
                                                    <BalanceInput
                                                        disabled={!isSelected || userRole === 'viewer' || isStartingBalanceLocked}
                                                        initialValue={stat.starting_balance}
                                                        onSave={(num) => handleUpdateYearlyBalance(stat.account_id, num)}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ opacity: isSelected ? 1 : 0.4 }}>
                                                    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                        <BalanceInput
                                                            disabled={!isSelected || userRole === 'viewer'}
                                                            initialValue={acc ? acc.start_amount : 0}
                                                            onSave={(num) => handleUpdateAccountStartAmount(stat.account_id, num)}
                                                        />
                                                        {acc?.account_type === AccountType.INVESTMENT && (
                                                            <Tooltip title={t('Add investment contribution')}>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={!isSelected || userRole === 'viewer'}
                                                                    onClick={() => handleOpenContributionDialog(acc, acc?.start_amount || 0)}
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        left: '100%',
                                                                        ml: 0.5,
                                                                        p: 0.25,
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                        borderRadius: 1.5,
                                                                        color: 'primary.main',
                                                                        bgcolor: 'background.paper',
                                                                        '&:hover': {
                                                                            bgcolor: 'primary.50',
                                                                            borderColor: 'primary.main'
                                                                        }
                                                                    }}
                                                                    aria-label="add-investment-contribution"
                                                                >
                                                                    <AddIcon sx={{ fontSize: '0.85rem' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.25 }}>
                                                        <Tooltip title={t("Click to change statement / balance date")}>
                                                            <Button
                                                                size="small"
                                                                onClick={(e) => handleOpenDatePopover(e, stat.account_id)}
                                                                disabled={!isSelected || userRole === 'viewer'}
                                                                sx={{
                                                                    p: '1px 6px',
                                                                    fontSize: '0.68rem',
                                                                    textTransform: 'none',
                                                                    color: 'text.secondary',
                                                                    borderRadius: 1,
                                                                    minWidth: 0,
                                                                    lineHeight: 1.2,
                                                                    border: '1px solid',
                                                                    borderColor: 'transparent',
                                                                    '&:hover': {
                                                                        bgcolor: 'action.hover',
                                                                        borderColor: 'divider',
                                                                        color: 'primary.main'
                                                                    }
                                                                }}
                                                                startIcon={<CalendarTodayIcon sx={{ fontSize: '0.75rem !important' }} />}
                                                            >
                                                                {t('As of')} {formatDateSafe(stat.balance_as_of || (acc && acc.balance_as_of) || acc?.created_at?.split('T')[0] || getTodayInTimezone(preferredTimezone), i18n.language)}
                                                            </Button>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, opacity: isSelected ? 1 : 0.4 }}>
                                                    {formatCurrency(stat.end_of_year_balance, stat.currency)}
                                                </TableCell>
                                                <TableCell align="right" sx={{
                                                    fontWeight: 700,
                                                    color: stat.difference >= 0 ? 'success.main' : 'error.main',
                                                    py: 1,
                                                    opacity: isSelected ? 1 : 0.4
                                                }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {stat.difference > 0 ? '+' : ''}{formatCurrency(stat.difference, stat.currency)}
                                                            </Typography>
                                                            <Typography component="span" variant="caption" sx={{ opacity: 0.7, fontWeight: 500 }}>
                                                                {stat.currency}
                                                            </Typography>
                                                        </Box>
                                                        {stat.currency !== forecast.currency && (
                                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                                                <Typography variant="caption" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
                                                                    ≈ {stat.difference_preferred > 0 ? '+' : ''}{formatCurrency(stat.difference_preferred, forecast.currency)}
                                                                </Typography>
                                                                <Typography component="span" variant="caption" sx={{ fontStyle: 'italic', opacity: 0.7, fontWeight: 500 }}>
                                                                    {forecast.currency}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {/* Total Row */}
                                    <TableRow sx={{ bgcolor: 'primary.50' }}>
                                        <TableCell colSpan={5} sx={{ fontWeight: 700 }}>{t('TOTAL SAVED / DEFICIT')}</TableCell>
                                        <TableCell align="right" sx={{
                                            fontWeight: 800,
                                            color: parseFloat(forecast.total_annual_savings) >= 0 ? 'success.main' : 'error.main'
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                {formatCurrency(forecast.total_annual_savings, forecast.currency)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ display: 'block', mt: -0.5, opacity: 0.7, fontWeight: 500 }}>
                                                {forecast.currency} {t('TOTAL')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}

            {/* ── Inflow/Outflow Bar Chart ── */}
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, mb: 6, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: 2
                }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Weekly Inflow vs Outflow')}</Typography>
                        <Typography variant="caption" color="text.secondary">{t('Green = Inflow · Red = Outflow')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <ToggleButtonGroup
                            value={chartRange}
                            exclusive
                            onChange={(e, newRange) => {
                                if (newRange) setChartRange(newRange);
                            }}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 1.5,
                                    py: 0.5,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    borderRadius: 1
                                }
                            }}
                            aria-label="forecast timeframe range"
                        >
                            <ToggleButton value={CashFlowRange.ONE_MONTH} aria-label="1 month">
                                {t('1 Month')}
                            </ToggleButton>
                            <ToggleButton value={CashFlowRange.THREE_MONTHS} aria-label="3 months">
                                {t('3 Months')}
                            </ToggleButton>
                            <ToggleButton value={CashFlowRange.SIX_MONTHS} aria-label="6 months">
                                {t('6 Months')}
                            </ToggleButton>
                            <ToggleButton value={CashFlowRange.ONE_YEAR} aria-label="1 year">
                                {t('1 Year')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Tooltip title={t("All projected income, expenses, and transfers are driven by the scheduled transactions below.")}>
                            <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {forecastLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : hasChartData ? (
                    <BarChart
                        slots={{
                            tooltip: CustomAxisTooltip,
                        }}
                        slotProps={{
                            tooltip: {
                                trigger: 'axis',
                                dataPoints: filteredDataPoints,
                                baseCurrency: forecast?.currency,
                                formatCurrency: formatCurrency,
                            },
                        }}
                        xAxis={[{
                            scaleType: 'band',
                            data: chartData.labels,
                            tickLabelInterval: (v, idx) => {
                                if (idx === 0) return true;
                                const dt = new Date(v + 'T12:00:00');
                                const prevV = chartData.labels[idx - 1];
                                const isFirstOfMonth = prevV && new Date(prevV + 'T12:00:00').getMonth() !== dt.getMonth();
                                return isFirstOfMonth;
                            },
                            valueFormatter: (v, context) => {
                                const dt = new Date(v + 'T12:00:00');
                                if (context.location === 'tick') {
                                    const month = dt.getMonth();
                                    return month === 0 ? dt.getFullYear().toString() : dt.toLocaleDateString(i18n.language, { month: 'short' });
                                }
                                return dt.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
                            },
                            tickLabelStyle: { fontSize: isMobile ? 9 : 10 },
                        }]}
                        yAxis={[{
                            valueFormatter: v => isMobile ? `$${(v / 1000).toFixed(0)}k` : formatCurrency(v),
                            tickLabelStyle: { fontSize: isMobile ? 9 : 11 },
                            width: isMobile ? 48 : 90,
                        }]}
                        series={[
                            {
                                data: chartData.credits,
                                label: t('Inflow'),
                                color: theme.palette.success.main,
                                valueFormatter: v => formatCurrency(v),
                                stack: 'flow',
                            },
                            {
                                data: chartData.debits.map(v => -v),
                                label: t('Outflow'),
                                color: theme.palette.error.main,
                                valueFormatter: v => formatCurrency(Math.abs(v)),
                                stack: 'flow',
                            },
                        ]}
                        height={300}
                        margin={{ top: 10, right: isMobile ? 10 : 20, bottom: 60, left: isMobile ? 55 : 100 }}
                        sx={{
                            [`.${axisClasses.left} .${axisClasses.label}`]: { transform: isMobile ? 'translateX(-20px)' : 'translateX(-45px)' },
                            width: '100%',
                        }}
                    />
                ) : (
                    <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
                        <AccountBalanceIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                        <Typography>{t('No forecast data yet. Add scheduled transactions below to start projecting.')}</Typography>
                    </Box>
                )}
            </Paper>

            {/* ── Running Balance Chart ── */}
            {hasChartData && !forecastLoading && (
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, mb: 6, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{t('Running Net Balance')}</Typography>
                    <LineChart
                        slots={{
                            tooltip: CustomDailyAxisTooltip,
                        }}
                        slotProps={{
                            tooltip: {
                                trigger: 'axis',
                                dataPoints: filteredDailyDataPoints,
                                baseCurrency: forecast?.currency,
                                formatCurrency: formatCurrency,
                            },
                        }}
                        xAxis={[{
                            scaleType: 'point',
                            data: dailyChartData.labels,
                            tickLabelInterval: (v, idx) => {
                                if (idx === 0) return true;
                                const dt = new Date(v + 'T12:00:00');
                                const prevV = dailyChartData.labels[idx - 1];
                                const isFirstOfMonth = prevV && new Date(prevV + 'T12:00:00').getMonth() !== dt.getMonth();
                                return isFirstOfMonth;
                            },
                            valueFormatter: (v, context) => {
                                const dt = new Date(v + 'T12:00:00');
                                if (context.location === 'tick') {
                                    const month = dt.getMonth();
                                    return month === 0 ? dt.getFullYear().toString() : dt.toLocaleDateString(i18n.language, { month: 'short' });
                                }
                                return dt.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
                            },
                            tickLabelStyle: { fontSize: isMobile ? 9 : 10 },
                        }]}
                        yAxis={[{
                            valueFormatter: v => isMobile ? `$${(v / 1000).toFixed(0)}k` : formatCurrency(v),
                            tickLabelStyle: { fontSize: isMobile ? 9 : 11 },
                            width: isMobile ? 48 : 90,
                        }]}
                        series={[{
                            data: dailyChartData.balance,
                            label: t('Cumulative Balance'),
                            color: theme.palette.primary.main,
                            showMark: false,
                            valueFormatter: v => formatCurrency(v),
                            area: true,
                        }]}
                        height={220}
                        margin={{ top: 10, right: isMobile ? 10 : 20, bottom: 60, left: isMobile ? 55 : 100 }}
                        sx={{ width: '100%' }}
                    />
                </Paper>
            )}

            {/* ── Scheduled Transactions Table ── */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 3,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Scheduled Transactions')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('These drive your cash flow forecast. Add recurring income, expenses, or one-time events.')}
                    </Typography>
                </Box>
                {userRole !== 'viewer' && (
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1, flexShrink: 0 }}>
                        <Button
                            id="copy-recurring-btn"
                            variant="outlined"
                            startIcon={<RepeatIcon />}
                            onClick={handleOpenCopyRecurringDialog}
                        >
                            {t('Copy from Recurring')}
                        </Button>
                        <Button
                            id="add-transaction-btn"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAdd}
                        >
                            {t('Add Transaction')}
                        </Button>
                    </Stack>
                )}
            </Box>

            {isMobile ? (
                <Box sx={{ mb: 6 }}>
                    {!isLoading && payments.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <EventIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.4, mb: 1, display: 'block', mx: 'auto' }} />
                            <Typography color="text.secondary">{t('No scheduled transactions yet.')}</Typography>
                            {userRole !== 'viewer' && (
                                <Button size="small" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ mt: 1 }}>
                                    {t('Add your first transaction')}
                                </Button>
                            )}
                        </Paper>
                    ) : (
                        payments.map(pmt => (
                            <ScheduledPaymentCard
                                key={pmt.id}
                                pmt={pmt}
                                formatDateSafe={formatDateSafe}
                                formatCurrency={formatCurrency}
                                onToggleStatus={handleToggleStatus}
                                onOpenMenu={handleOpenPaymentMenu}
                                isViewer={userRole === 'viewer'}
                            />
                        ))
                    )}
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{
                    mb: 6, overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1
                }}>
                    <Table sx={{ minWidth: 500 }}>
                        <TableHead sx={{ bgcolor: 'background.default' }}>
                            <TableRow>
                                <TableCell>{t('Status')}</TableCell>
                                <TableCell>{t('Description')}</TableCell>
                                <TableCell>{t('Account')}</TableCell>
                                <TableCell>{t('Type')}</TableCell>
                                <TableCell>{t('Schedule')}</TableCell>
                                <TableCell align="right">{t('Amount')}</TableCell>
                                {userRole !== 'viewer' && <TableCell align="right">{t('Actions')}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!isLoading && payments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={userRole !== 'viewer' ? 7 : 6} align="center" sx={{ py: 6 }}>
                                        <EventIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.4, mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography color="text.secondary">{t('No scheduled transactions yet.')}</Typography>
                                        {userRole !== 'viewer' && (
                                            <Button size="small" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ mt: 1 }}>
                                                {t('Add your first transaction')}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                payments.map(pmt => (
                                    <ScheduledPaymentRow
                                        key={pmt.id}
                                        pmt={pmt}
                                        formatDateSafe={formatDateSafe}
                                        formatCurrency={formatCurrency}
                                        onToggleStatus={handleToggleStatus}
                                        onOpenMenu={handleOpenPaymentMenu}
                                        isViewer={userRole === 'viewer'}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Recurring Income hint banner removed as forecast is self-contained */}

            {/* ── Action Menu ── */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => handleOpenEdit(payments.find(p => p.id === selectedPaymentId))}>{t('Edit')}</MenuItem>
                <MenuItem
                    onClick={() => { setAnchorEl(null); setDeleteDialogOpen(true); }}
                    sx={{ color: 'error.main' }}
                >
                    {t('Delete')}
                </MenuItem>
            </Menu>

            {/* ── Add/Edit Dialog ── */}
            <FormDialog
                open={dialogOpen}
                onClose={isSaving ? undefined : () => { setDialogOpen(false); resetForm(); }}
                onSubmit={handleSave}
                title={currentPayment ? t('Edit Scheduled Transaction') : t('Add Scheduled Transaction')}
                subtitle={t("Configure your future income, expense, or internal transfer.")}
                saving={isSaving}
                maxWidth="sm"
            >
                <Stack spacing={4}>
                    {/* Description Section */}
                    <FormSection title={t("Transaction Details")}>
                        <Stack spacing={3}>
                            <FormField label={t("Description")} required>
                                <TextField
                                    autoFocus
                                    variant="filled"
                                    fullWidth
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    sx={filledInputSx}
                                />
                            </FormField>

                            <FormField label={t("Type")}>
                                <ToggleButtonGroup
                                    value={formData.action}
                                    exclusive
                                    onChange={(_, v) => { if (v) setFormData({ ...formData, action: v }); }}
                                    fullWidth
                                >
                                    <ToggleButton value={ScheduledTransactionAction.OUTFLOW} color="error">
                                        <TrendingDownIcon sx={{ mr: 1, fontSize: 18 }} /> {t("Expense")}
                                    </ToggleButton>
                                    <ToggleButton value={ScheduledTransactionAction.INFLOW} color="success">
                                        <TrendingUpIcon sx={{ mr: 1, fontSize: 18 }} /> {t("Income")}
                                    </ToggleButton>
                                    <ToggleButton value={ScheduledTransactionAction.MOVE} color="primary">
                                        <SwapHorizIcon sx={{ mr: 1, fontSize: 18 }} /> {t("Transfer")}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </FormField>

                            <Stack direction="row" spacing={3}>
                                <FormField label={t("Amount")} required sx={{ flex: 2 }}>
                                    <TextField
                                        variant="filled"
                                        fullWidth
                                        slotProps={{
                                            htmlInput: { inputMode: 'decimal' }
                                        }}
                                        value={formData.amount}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start" sx={{ color: arborGreen, fontWeight: 700, height: '100%', mt: 0 }}>$</InputAdornment>,
                                            disableUnderline: true
                                        }}
                                        onFocus={(e) => handleNumericFocus(e)}
                                        onChange={e => setFormData({ ...formData, amount: formatCurrencyInput(e.target.value) })}
                                        sx={filledInputSx}
                                    />
                                </FormField>
                                <FormField label={t("Currency")} required sx={{ flex: 1 }}>
                                    <Autocomplete
                                        options={[Currency.CAD, Currency.USD, Currency.CNY]}
                                        value={formData.currency || Currency.CAD}
                                        onChange={(e, newCurrency) => {
                                            if (!newCurrency) return;
                                            const matchingAccounts = accounts.filter(acc => acc.currency === newCurrency);
                                            const defaultAccount = matchingAccounts.length > 0 ? matchingAccounts[0].id : '';
                                            setFormData({
                                                ...formData,
                                                currency: newCurrency,
                                                from_account_id: defaultAccount,
                                                to_account_id: defaultAccount
                                            });
                                        }}
                                        disableClearable
                                        autoHighlight
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="filled"
                                                fullWidth
                                                sx={filledInputSx}
                                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Stack>
                        </Stack>
                    </FormSection>

                    {/* Account Section */}
                    <FormSection title={t("Account Routing")}>
                        <Stack spacing={3}>
                            {(formData.action === ScheduledTransactionAction.OUTFLOW || formData.action === ScheduledTransactionAction.MOVE) && (
                                <FormField label={t("Source Account")} required>
                                    <TextField
                                        select
                                        variant="filled"
                                        fullWidth
                                        value={formData.from_account_id}
                                        onChange={e => setFormData({ ...formData, from_account_id: e.target.value })}
                                        sx={filledInputSx}
                                        SelectProps={{ disableUnderline: true }}
                                    >
                                        {accounts.filter(acc => acc.currency === formData.currency).map(acc => (
                                            <MenuItem key={acc.id} value={acc.id}>
                                                {t(acc.account_type)} ({acc.currency})
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </FormField>
                            )}

                            {(formData.action === ScheduledTransactionAction.INFLOW || formData.action === ScheduledTransactionAction.MOVE) && (
                                <FormField label={t("Destination Account")} required>
                                    <TextField
                                        select
                                        variant="filled"
                                        fullWidth
                                        value={formData.to_account_id}
                                        onChange={e => setFormData({ ...formData, to_account_id: e.target.value })}
                                        sx={filledInputSx}
                                        SelectProps={{ disableUnderline: true }}
                                    >
                                        {accounts.filter(acc => acc.currency === formData.currency).map(acc => (
                                            <MenuItem key={acc.id} value={acc.id}>
                                                {t(acc.account_type)} ({acc.currency})
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </FormField>
                            )}
                        </Stack>
                    </FormSection>

                    {/* Schedule Section */}
                    <FormSection title={t("Schedule Configuration")}>
                        <Stack spacing={3}>
                            <FormField label={t("Frequency")}>
                                <ToggleButtonGroup
                                    value={formData.payment_type}
                                    exclusive
                                    onChange={(_, v) => {
                                        if (!v) return;
                                        setFormData({
                                            ...formData,
                                            payment_type: v,
                                            interval: v === ScheduledTransactionType.RECURRING ? RecurringInterval.MONTHLY : null,
                                            interval_config: null,
                                        });
                                    }}
                                    fullWidth
                                    sx={{}}
                                >
                                    <ToggleButton value={ScheduledTransactionType.ONE_TIME}>
                                        <EventIcon sx={{ mr: 1, fontSize: 18 }} />{t("One-time")}
                                    </ToggleButton>
                                    <ToggleButton value={ScheduledTransactionType.RECURRING}>
                                        <RepeatIcon sx={{ mr: 1, fontSize: 18 }} />{t("Recurring")}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </FormField>

                            {formData.payment_type === ScheduledTransactionType.RECURRING && (
                                <FormField label={t("Interval")} required>
                                    <TextField
                                        select
                                        variant="filled"
                                        fullWidth
                                        value={formData.interval || ''}
                                        onChange={e => {
                                            const v = e.target.value;
                                            let cfg = null;
                                            if (v === RecurringInterval.DAYS_OF_WEEK) cfg = { days_of_week: [] };
                                            if (v === RecurringInterval.DAYS_OF_MONTH) cfg = { days_of_month: [] };
                                            setFormData({ ...formData, interval: v, interval_config: cfg });
                                        }}
                                        sx={filledInputSx}
                                        SelectProps={{ disableUnderline: true }}
                                    >
                                        {Object.entries(INTERVAL_LABELS).map(([k, lbl]) => (
                                            <MenuItem key={k} value={k}>{t(lbl)}</MenuItem>
                                        ))}
                                    </TextField>
                                </FormField>
                            )}

                            {/* Days pickers (inline with minimal styling) */}
                            {formData.payment_type === ScheduledTransactionType.RECURRING && formData.interval === RecurringInterval.DAYS_OF_WEEK && (
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'rgba(10, 42, 33, 0.5)' }}>
                                        {t("Select Days")}
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={1}>
                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                                            const sel = formData.interval_config?.days_of_week?.includes(idx);
                                            return (
                                                <Chip
                                                    key={day}
                                                    label={t(day)}
                                                    clickable
                                                    onClick={() => {
                                                        const cur = formData.interval_config?.days_of_week || [];
                                                        const next = sel ? cur.filter(d => d !== idx) : [...cur, idx].sort();
                                                        setFormData({ ...formData, interval_config: { days_of_week: next } });
                                                    }}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        fontWeight: 600,
                                                        bgcolor: sel ? arborGreen : 'rgba(10, 42, 33, 0.05)',
                                                        color: sel ? '#fff' : 'rgba(10, 42, 33, 0.6)',
                                                        '&:hover': { bgcolor: sel ? '#061a15' : 'rgba(10, 42, 33, 0.1)' }
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            )}

                            {formData.payment_type === ScheduledTransactionType.RECURRING && formData.interval === RecurringInterval.DAYS_OF_MONTH && (
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'rgba(10, 42, 33, 0.5)' }}>
                                        {t("Select Day(s) of Month")}
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => {
                                            const sel = formData.interval_config?.days_of_month?.includes(day);
                                            return (
                                                <Button
                                                    key={day}
                                                    size="small"
                                                    variant={sel ? 'contained' : 'outlined'}
                                                    sx={{
                                                        minWidth: 0, p: 0.5, aspectRatio: '1/1', fontSize: '0.75rem', borderRadius: 1.5,
                                                        bgcolor: sel ? arborGreen : 'transparent',
                                                        color: sel ? '#fff' : arborGreen,
                                                        borderColor: 'rgba(10, 42, 33, 0.1)',
                                                        '&:hover': { bgcolor: sel ? '#061a15' : 'rgba(10, 42, 33, 0.05)', borderColor: arborGreen }
                                                    }}
                                                    onClick={() => {
                                                        const cur = formData.interval_config?.days_of_month || [];
                                                        const next = sel ? cur.filter(d => d !== day) : [...cur, day].sort((a, b) => a - b);
                                                        setFormData({ ...formData, interval_config: { days_of_month: next } });
                                                    }}
                                                >
                                                    {day}
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                                <FormField label={formData.payment_type === ScheduledTransactionType.ONE_TIME ? t('Transaction Date') : t('Start Date')} required sx={{ flex: 1 }}>
                                    <TextField
                                        variant="filled"
                                        type="date"
                                        fullWidth
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.start_date}
                                        onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                                        sx={filledInputSx}
                                    />
                                </FormField>
                                {formData.payment_type === ScheduledTransactionType.RECURRING && (
                                    <FormField label={t("End Date (Optional)")} sx={{ flex: 1 }}>
                                        <TextField
                                            variant="filled"
                                            type={formData.end_date ? 'date' : 'text'}
                                            onFocus={e => (e.target.type = 'date')}
                                            onBlur={e => (e.target.type = formData.end_date ? 'date' : 'text')}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={formData.end_date}
                                            onChange={e => setFormData({ ...formData, end_date: e.target.value })}
                                            sx={filledInputSx}
                                            placeholder={t("Never")}
                                        />
                                    </FormField>
                                )}
                            </Stack>
                        </Stack>
                    </FormSection>

                    {/* Categorization Section */}
                    <FormSection title={t("Organization")}>
                        <Stack spacing={3}>
                            {formData.action !== 'MOVE' && (
                                <FormField label={t("Category (Optional)")}>
                                    <Autocomplete
                                        options={sortCategoriesHierarchically(categories, t)}
                                        getOptionLabel={(opt) => getCategoryOptionLabel(opt, categories, t)}
                                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                        value={categories.find(c => c.id === formData.category_id) || null}
                                        onChange={(_, v) => setFormData({ ...formData, category_id: v ? v.id : '' })}
                                        renderOption={(props, option) => {
                                            const { key, ...restProps } = props;
                                            const paddingLeft = option.parent_id ? '24px' : '0px';
                                            return (
                                                <li key={key || option.id} {...restProps}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: paddingLeft }}>
                                                        {option.color && (
                                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: option.color }} />
                                                        )}
                                                        {getCategoryOptionLabel(option, categories, t)}
                                                    </Box>
                                                </li>
                                            );
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant="filled"
                                                fullWidth
                                                sx={filledInputSx}
                                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                            />
                                        )}
                                    />
                                </FormField>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.is_active}
                                        onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                        sx={{ color: arborGreen, '&.Mui-checked': { color: arborGreen } }}
                                    />
                                }
                                label={<Typography variant="body2" sx={{ fontWeight: 600, color: arborGreen }}>{t("Transaction is Active")}</Typography>}
                            />
                        </Stack>
                    </FormSection>
                </Stack>
            </FormDialog>

            {/* ── Delete Confirm Dialog ── */}
            <FormDialog
                open={deleteDialogOpen}
                onClose={isDeleting ? undefined : () => { setDeleteDialogOpen(false); setSelectedPaymentId(null); }}
                onSubmit={handleDelete}
                title={t("Remove Scheduled Transaction?")}
                subtitle={t("This action will remove the transaction from your forecast calculations permanently.")}
                saveLabel={t("Delete")}
                saveDisabled={false}
                saving={isDeleting}
                maxWidth="xs"
            >
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {t("This action cannot be undone. All future projections associated with this transaction will be removed.")}
                </Alert>
            </FormDialog>

            {/* ── Delete Account Confirm Dialog ── */}
            <FormDialog
                open={Boolean(accountToDelete)}
                onClose={isDeletingAccount ? undefined : () => setAccountToDelete(null)}
                onSubmit={handleDeleteAccountConfirm}
                title={t("Delete Account?")}
                subtitle={t("Are you sure you want to delete this account?")}
                saveLabel={t("Delete")}
                saveDisabled={false}
                saving={isDeletingAccount}
                maxWidth="xs"
            >
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {t("This action cannot be undone. Any linked scheduled transactions will have their account references removed.")}
                </Alert>
            </FormDialog>

            {/* ── Copy from Recurring Rules Dialog ── */}
            <FormDialog
                open={copyRecurringOpen}
                onClose={isImporting ? undefined : () => setCopyRecurringOpen(false)}
                onSubmit={handleCopyRecurringSubmit}
                title={t("Copy from Recurring Rules")}
                subtitle={t("Select the recurring rules to copy into your Cash Flow Forecast.")}
                saveLabel={`${t("Import Selected")} (${selectedRuleIds.length})`}
                saveDisabled={selectedRuleIds.length === 0}
                saving={isImporting}
                maxWidth="sm"
            >
                <Stack spacing={2}>
                    {recurringRulesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : recurringRules.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {t("No recurring rules found in this ledger.")}
                            </Typography>
                            <Button
                                variant="text"
                                onClick={() => navigate(`/ledger/${ledgerId}/recurring`)}
                                sx={{ mt: 1, textTransform: 'none' }}
                            >
                                {t("Create Recurring Rule")}
                            </Button>
                        </Box>
                    ) : (
                        <List sx={{
                            width: '100%',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 0,
                            overflow: 'hidden',
                            maxHeight: '400px',
                            overflowY: 'auto'
                        }}>
                            {recurringRules.map((rule) => {
                                const alreadyImported = isAlreadyImported(rule);
                                const isSelected = selectedRuleIds.includes(rule.id);
                                return (
                                    <ListItem
                                        key={rule.id}
                                        onClick={() => {
                                            if (alreadyImported) return;
                                            setSelectedRuleIds(prev =>
                                                prev.includes(rule.id)
                                                    ? prev.filter(id => id !== rule.id)
                                                    : [...prev, rule.id]
                                            );
                                        }}
                                        sx={{
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            '&:last-child': { borderBottom: 'none' },
                                            cursor: alreadyImported ? 'default' : 'pointer',
                                            opacity: alreadyImported ? 0.7 : 1,
                                            bgcolor: isSelected ? 'action.selected' : 'transparent',
                                            '&:hover': {
                                                bgcolor: alreadyImported ? 'transparent' : 'action.hover'
                                            },
                                            py: 1.5,
                                            px: 2
                                        }}
                                        secondaryAction={
                                            alreadyImported ? (
                                                <Chip size="small" label={t("Already in Forecast")} variant="outlined" color="default" sx={{ fontWeight: 600 }} />
                                            ) : (
                                                <Checkbox
                                                    edge="end"
                                                    checked={isSelected}
                                                    sx={{ pointerEvents: 'none' }}
                                                />
                                            )
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    {rule.description}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <Chip
                                                        size="small"
                                                        label={INTERVAL_LABELS[rule.interval] ? t(INTERVAL_LABELS[rule.interval]) : rule.interval}
                                                        sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                                    />
                                                    {rule.category && (
                                                        <Chip
                                                            size="small"
                                                            label={t(rule.category.name)}
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.65rem',
                                                                bgcolor: rule.category.color ? `${rule.category.color}15` : 'transparent',
                                                                color: rule.category.color || 'primary.main',
                                                                borderColor: rule.category.color || 'primary.main',
                                                                border: 1
                                                             }}
                                                        />
                                                    )}
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: Number(rule.amount) < 0 ? 'success.main' : 'text.primary' }}>
                                                        {formatCurrency(rule.amount, rule.currency)} {rule.currency}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Stack>
            </FormDialog>


            {/* ── Account Management Dialog ── */}
            <FormDialog
                open={accountManagementOpen}
                onClose={isAccountSubmitting || isDeletingAccount ? undefined : () => {
                    setAccountManagementOpen(false);
                    setAccountFormOpen(false);
                    setEditingAccount(null);
                }}
                title={t("Manage Accounts")}
                subtitle={t("Configure multiple accounts (Savings, Investment, etc.) and track internal transfers.")}
                saveLabel={t("Close")}
                saveDisabled={isAccountSubmitting || isDeletingAccount}
                onSubmit={isAccountSubmitting || isDeletingAccount ? () => { } : () => {
                    setAccountManagementOpen(false);
                    setAccountFormOpen(false);
                    setEditingAccount(null);
                }}
                maxWidth="sm"
            >
                <Stack spacing={4}>
                    {/* Account List */}
                    <FormSection title={t("Current Accounts")}>
                        <Stack spacing={2}>
                            {accounts.length === 0 ? (
                                <Typography variant="body2" sx={{ color: 'rgba(10, 42, 33, 0.4)', py: 2, textAlign: 'center' }}>
                                    {t("No accounts configured. Add one below to start tracking.")}
                                </Typography>
                            ) : (
                                accounts.map(acc => {
                                    const isEditing = editingAccount && editingAccount.id === acc.id;
                                    if (isEditing) {
                                        return (
                                            <Paper
                                                key={acc.id}
                                                elevation={0}
                                                sx={{
                                                    p: 2.5,
                                                    bgcolor: 'rgba(10, 42, 33, 0.02)',
                                                    borderRadius: 2.5,
                                                    border: '1px solid rgba(10, 42, 33, 0.08)'
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: arborGreen }}>
                                                    {t("Edit")} {t(acc.account_type)}
                                                </Typography>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: 2,
                                                    mb: 2
                                                }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <FormField label={t("Account Type")} required>
                                                            <TextField
                                                                autoFocus
                                                                select
                                                                variant="filled"
                                                                fullWidth
                                                                value={accountFormData.account_type}
                                                                onChange={e => setAccountFormData({ ...accountFormData, account_type: e.target.value })}
                                                                sx={filledInputSx}
                                                                SelectProps={{ disableUnderline: true }}
                                                            >
                                                                {Object.keys(AccountType).map(type => (
                                                                    <MenuItem key={type} value={type}>{t(type)}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </FormField>
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <FormField label={t("Currency")} required>
                                                            <Autocomplete
                                                                options={[Currency.CAD, Currency.USD, Currency.CNY]}
                                                                value={accountFormData.currency || Currency.CAD}
                                                                onChange={(e, newValue) => {
                                                                    if (newValue) setAccountFormData(prev => ({ ...prev, currency: newValue }));
                                                                }}
                                                                disableClearable
                                                                autoHighlight
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        variant="filled"
                                                                        fullWidth
                                                                        sx={filledInputSx}
                                                                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                                    />
                                                                )}
                                                            />
                                                        </FormField>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                                                    <Button
                                                        disabled={isAccountSubmitting}
                                                        onClick={() => {
                                                            setEditingAccount(null);
                                                            setAccountFormData({ account_type: AccountType.CHECKING, currency: 'CAD' });
                                                        }}
                                                        sx={{
                                                            color: arborGreen,
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            '&:hover': { bgcolor: 'rgba(10, 42, 33, 0.05)' }
                                                        }}
                                                    >
                                                        {t("Cancel")}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleSaveAccount}
                                                        disabled={isAccountSubmitting}
                                                        startIcon={isAccountSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                                                        disableElevation
                                                        sx={{
                                                            bgcolor: arborGreen,
                                                            fontWeight: 700,
                                                            textTransform: 'none',
                                                            py: 1,
                                                            px: 4,
                                                            '&:hover': { bgcolor: '#061a15' }
                                                        }}
                                                    >
                                                        {isAccountSubmitting ? t('Saving...') : t('Save')}
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        );
                                    }

                                    return (
                                        <Paper
                                            key={acc.id}
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                bgcolor: 'rgba(10, 42, 33, 0.03)',
                                                borderRadius: 2.5,
                                                border: '1px solid rgba(10, 42, 33, 0.05)',
                                                transition: 'all 0.2s ease',
                                                '&:hover': { bgcolor: 'rgba(10, 42, 33, 0.05)', borderColor: 'rgba(10, 42, 33, 0.1)' }
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 700, color: arborGreen }}>
                                                    {t(acc.account_type)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(10, 42, 33, 0.6)', fontWeight: 500 }}>
                                                    {acc.currency} · {t('Starts at')} {formatCurrency(acc.start_amount, acc.currency)}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1}>
                                                <IconButton
                                                    size="small"
                                                    disabled={isAccountSubmitting || isDeletingAccount}
                                                    onClick={() => {
                                                        setEditingAccount(acc);
                                                        setAccountFormData({
                                                            account_type: acc.account_type,
                                                            currency: acc.currency,
                                                        });
                                                        setAccountFormOpen(false);
                                                    }}
                                                    sx={{ color: arborGreen, opacity: 0.6, '&:hover': { opacity: 1, bgcolor: 'rgba(10, 42, 33, 0.05)' } }}
                                                >
                                                    <SettingsIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={isAccountSubmitting || isDeletingAccount}
                                                    onClick={() => handleDeleteAccount(acc.id)}
                                                    sx={{ opacity: 0.6, '&:hover': { opacity: 1, bgcolor: 'rgba(211, 47, 47, 0.05)' } }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Paper>
                                    );
                                })
                            )}

                            {!accountFormOpen && !editingAccount && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    disabled={isAccountSubmitting || isDeletingAccount}
                                    onClick={() => {
                                        setEditingAccount(null);
                                        setAccountFormData({
                                            account_type: AccountType.CHECKING,
                                            currency: 'CAD',
                                        });
                                        setAccountFormOpen(true);
                                    }}
                                    fullWidth
                                    sx={{
                                        py: 1.5,
                                        borderColor: 'rgba(10, 42, 33, 0.2)',
                                        color: arborGreen,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: 2.5,
                                        '&:hover': {
                                            borderColor: arborGreen,
                                            bgcolor: 'rgba(10, 42, 33, 0.02)'
                                        }
                                    }}
                                >
                                    {t("Add Account")}
                                </Button>
                            )}
                        </Stack>
                    </FormSection>

                    {/* Add Form Section */}
                    <Collapse in={accountFormOpen && !editingAccount}>
                        <FormSection
                            title={t("Add New Account")}
                            sx={{
                                p: 3,
                                borderRadius: 4,
                                bgcolor: 'rgba(10, 42, 33, 0.02)',
                                border: '1px solid rgba(10, 42, 33, 0.05)'
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                mb: 2
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <FormField label={t("Account Type")} required>
                                        <TextField
                                            autoFocus
                                            select
                                            variant="filled"
                                            fullWidth
                                            value={accountFormData.account_type}
                                            onChange={e => setAccountFormData({ ...accountFormData, account_type: e.target.value })}
                                            sx={filledInputSx}
                                            SelectProps={{ disableUnderline: true }}
                                        >
                                            {Object.keys(AccountType).map(type => (
                                                <MenuItem key={type} value={type}>{t(type)}</MenuItem>
                                            ))}
                                        </TextField>
                                    </FormField>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <FormField label={t("Currency")} required>
                                        <Autocomplete
                                            options={[Currency.CAD, Currency.USD, Currency.CNY]}
                                            value={accountFormData.currency || Currency.CAD}
                                            onChange={(e, newValue) => {
                                                if (newValue) setAccountFormData(prev => ({ ...prev, currency: newValue }));
                                            }}
                                            disableClearable
                                            autoHighlight
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="filled"
                                                    fullWidth
                                                    sx={filledInputSx}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                />
                                            )}
                                        />
                                    </FormField>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                                <Button
                                    disabled={isAccountSubmitting}
                                    onClick={() => {
                                        setEditingAccount(null);
                                        setAccountFormOpen(false);
                                        setAccountFormData({ account_type: AccountType.CHECKING, currency: 'CAD' });
                                    }}
                                    sx={{
                                        color: arborGreen,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        py: 1,
                                        '&:hover': { bgcolor: 'rgba(10, 42, 33, 0.05)' }
                                    }}
                                >
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveAccount}
                                    disabled={isAccountSubmitting}
                                    startIcon={isAccountSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                                    disableElevation
                                    sx={{
                                        bgcolor: arborGreen,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        py: 1,
                                        px: 4,
                                        '&:hover': { bgcolor: '#061a15' }
                                    }}
                                >
                                    {isAccountSubmitting ? t('Creating...') : t('Create')}
                                </Button>
                            </Box>
                        </FormSection>
                    </Collapse>
                </Stack>
            </FormDialog>

            {/* ── Balance As Of Date Picker Popover (Option 2 Demo) ── */}
            <Popover
                open={Boolean(asOfDateAnchorEl)}
                anchorEl={asOfDateAnchorEl}
                onClose={handleCloseDatePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 2.5, width: 280, borderRadius: 2, boxShadow: '0px 8px 24px rgba(0,0,0,0.15)' } } }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('Balance Statement Date')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {t('Specify when this account balance was recorded.')}
                </Typography>
                <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={tempAsOfDate}
                    onChange={(e) => setTempAsOfDate(e.target.value)}
                    sx={{ mb: 1.5 }}
                />
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', py: 0.25, px: 1, textTransform: 'none' }}
                        onClick={() => setTempAsOfDate(getTodayInTimezone(preferredTimezone))}
                    >
                        {t('Today')}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', py: 0.25, px: 1, textTransform: 'none' }}
                        onClick={() => {
                            const now = new Date();
                            const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
                            setTempAsOfDate(lastDayPrevMonth.toISOString().split('T')[0]);
                        }}
                    >
                        {t('Month-End')}
                    </Button>
                </Stack>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button size="small" onClick={handleCloseDatePopover} sx={{ textTransform: 'none' }}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSaveAsOfDate(tempAsOfDate)}
                        sx={{ bgcolor: arborGreen, textTransform: 'none', '&:hover': { bgcolor: '#061a15' } }}
                    >
                        {t('Apply')}
                    </Button>
                </Box>
            </Popover>

            {/* ── Investment Contribution Dialog ── */}
            <AddContributionDialog
                open={contributionDialogOpen}
                onClose={handleCloseContributionDialog}
                account={contributionAccount}
                currentBalance={contributionCurrentBalance}
                formatCurrency={formatCurrency}
                ledgerId={ledgerId}
                api={api}
                preferredCurrency={preferredCurrency}
                preferredTimezone={preferredTimezone}
                onSuccess={async () => {
                    await Promise.all([fetchAccounts(), fetchForecast()]);
                }}
                showToast={showToast}
                userRole={userRole}
            />
        </Container>
    );
}
